import React, { useState, useEffect } from 'react';
import { collection, addDoc, onSnapshot, query, orderBy, where, getDocs, doc, updateDoc, arrayUnion, arrayRemove, deleteDoc } from 'firebase/firestore';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, onAuthStateChanged, signOut, updateProfile, sendEmailVerification, sendPasswordResetEmail, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { ref, uploadString, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, auth, storage } from './firebase'; 

// ==========================================
// 1. COMPONENTES REUTILIZABLES (UI EXTERNA)
// ==========================================

const ToggleSwitch = ({ activo, onChange }) => (
  <button onClick={onChange} className={`w-11 h-6 rounded-full relative transition-colors duration-300 focus:outline-none ${activo ? 'bg-emerald-500' : 'bg-slate-300'}`}>
    <span className={`absolute left-1 top-1 w-4 h-4 rounded-full bg-white transition-transform duration-300 shadow-sm ${activo ? 'translate-x-5' : 'translate-x-0'}`}></span>
  </button>
);

const ModalConfirm = ({ show, icon, title, msg, btnText, onConfirm, onCancel, color="rose" }) => {
  if (!show) return null;
  const bgBtn = color === 'rose' ? 'bg-rose-600 hover:bg-rose-700 shadow-rose-600/30' : color === 'emerald' ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/30' : 'bg-slate-900 hover:bg-slate-800 shadow-slate-900/30';
  const border = color === 'rose' ? 'border-rose-100' : color === 'emerald' ? 'border-emerald-100' : 'border-amber-100';
  const bgIcon = color === 'rose' ? 'bg-rose-100 text-rose-500' : color === 'emerald' ? 'bg-emerald-100 text-emerald-500' : 'bg-amber-100 text-amber-500';
  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-[200] p-4 print:hidden" onClick={(e) => e.stopPropagation()}>
      <div className={`bg-white w-full max-w-sm rounded-[32px] p-8 shadow-2xl border-4 ${border} text-center animate-in zoom-in-95 duration-200`}>
        <div className={`h-16 w-16 rounded-full ${bgIcon} flex items-center justify-center text-3xl mx-auto mb-4`}>{icon}</div>
        <h3 className="text-xl font-black text-slate-900 mb-2 tracking-tight">{title}</h3>
        <div className="text-sm text-slate-500 mb-8 font-medium leading-relaxed">{msg}</div>
        <div className="flex flex-col gap-2">
          <button onClick={onConfirm} className={`w-full py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-transform text-white shadow-lg active:scale-95 ${bgBtn}`}>{btnText}</button>
          <button onClick={onCancel} className="w-full text-slate-500 font-bold py-3 text-xs uppercase tracking-widest hover:bg-slate-50 rounded-2xl transition-colors active:scale-95">Cancelar</button>
        </div>
      </div>
    </div>
  );
};

const ModalAlerta = ({ config, onClose }) => {
  if (!config) return null;
  const bgBtn = config.color === 'rose' ? 'bg-rose-600 hover:bg-rose-700 shadow-rose-600/30' : config.color === 'emerald' ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/30' : 'bg-slate-900 hover:bg-slate-800 shadow-slate-900/30';
  const border = config.color === 'rose' ? 'border-rose-100' : config.color === 'emerald' ? 'border-emerald-100' : 'border-amber-100';
  const bgIcon = config.color === 'rose' ? 'bg-rose-100 text-rose-500' : config.color === 'emerald' ? 'bg-emerald-100 text-emerald-500' : 'bg-amber-100 text-amber-500';
  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-[300] p-4 print:hidden" onClick={(e) => e.stopPropagation()}>
      <div className={`bg-white w-full max-w-sm rounded-[32px] p-8 shadow-2xl border-4 ${border} text-center animate-in zoom-in-95 duration-200`}>
        <div className={`h-16 w-16 rounded-full ${bgIcon} flex items-center justify-center text-3xl mx-auto mb-4`}>{config.icon}</div>
        <h3 className="text-xl font-black text-slate-900 mb-2 tracking-tight">{config.title}</h3>
        <div className="text-sm text-slate-500 mb-8 font-medium leading-relaxed">{config.msg}</div>
        <button onClick={onClose} className={`w-full py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-transform text-white shadow-lg active:scale-95 ${bgBtn}`}>Entendido</button>
      </div>
    </div>
  );
};

const DashboardSkeleton = () => (
  <div className="flex h-[100dvh] w-full bg-[#F8FAFC] font-sans overflow-hidden">
    <aside className="hidden md:flex flex-col w-[280px] bg-[#0F172A] border-r border-slate-800 shrink-0 relative overflow-hidden">
       <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent animate-pulse"></div>
       <div className="h-24 flex items-center px-8 border-b border-slate-800"><div className="h-10 w-10 rounded-[14px] bg-slate-800 animate-pulse"></div><div className="h-6 w-32 bg-slate-800 rounded ml-3 animate-pulse"></div></div>
       <div className="p-5 space-y-4 mt-4"><div className="h-12 bg-slate-800 rounded-xl animate-pulse"></div><div className="h-12 bg-slate-800/50 rounded-xl animate-pulse"></div><div className="h-12 bg-slate-800/50 rounded-xl animate-pulse"></div></div>
    </aside>
    <main className="flex-1 flex flex-col h-full">
      <header className="h-20 md:h-24 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-10 shrink-0"><div className="h-8 w-32 bg-slate-100 rounded-lg animate-pulse"></div><div className="flex items-center gap-3"><div className="h-10 w-10 bg-slate-100 rounded-full animate-pulse"></div><div className="h-10 w-10 bg-slate-100 rounded-full animate-pulse"></div></div></header>
      <div className="flex-1 p-4 md:p-10 space-y-8 overflow-y-auto">
         <div className="max-w-6xl mx-auto space-y-8">
            <div><div className="h-10 w-64 bg-slate-200 rounded-xl animate-pulse mb-2"></div><div className="h-4 w-48 bg-slate-100 rounded animate-pulse"></div></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="h-36 bg-white rounded-[24px] border border-slate-100 shadow-sm p-6"><div className="h-3 w-24 bg-slate-100 rounded mb-4 animate-pulse"></div><div className="h-10 w-40 bg-slate-200 rounded-xl animate-pulse"></div></div>
              <div className="h-36 bg-white rounded-[24px] border border-slate-100 shadow-sm p-6"><div className="h-3 w-24 bg-slate-100 rounded mb-4 animate-pulse"></div><div className="h-10 w-40 bg-slate-200 rounded-xl animate-pulse"></div></div>
              <div className="h-36 bg-white rounded-[24px] border border-slate-100 shadow-sm p-6"><div className="h-3 w-24 bg-slate-100 rounded mb-4 animate-pulse"></div><div className="h-10 w-40 bg-slate-200 rounded-xl animate-pulse"></div></div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
               <div className="lg:col-span-2 h-96 bg-white rounded-[32px] border border-slate-100 shadow-sm p-6"><div className="h-4 w-32 bg-slate-100 rounded animate-pulse mb-6"></div><div className="space-y-4"><div className="h-16 bg-slate-50 rounded-2xl animate-pulse"></div><div className="h-16 bg-slate-50 rounded-2xl animate-pulse"></div><div className="h-16 bg-slate-50 rounded-2xl animate-pulse"></div></div></div>
               <div className="lg:col-span-1 h-80 bg-slate-900 rounded-[32px] p-8 relative overflow-hidden"><div className="absolute inset-0 bg-white/5 animate-pulse"></div><div className="h-6 w-40 bg-white/10 rounded animate-pulse mb-8 relative z-10"></div><div className="space-y-3 relative z-10"><div className="h-14 bg-white/5 rounded-xl animate-pulse"></div><div className="h-14 bg-white/5 rounded-xl animate-pulse"></div></div></div>
            </div>
         </div>
      </div>
    </main>
  </div>
);

const TransactionItem = ({ trx, grupo, miEmail, miNombre, onSelect, implicadosGrupo, formateador }) => {
  const involucrados = trx.involucradosEmails || []; 
  const cuota = trx.monto / (involucrados.length > 0 ? involucrados.length : 1);
  const esPagador = (trx.pagadorEmail && trx.pagadorEmail === miEmail) || (!trx.pagadorEmail && trx.persona === miNombre);
  const estoyInvolucrado = involucrados.length === 0 ? true : involucrados.includes(miEmail);
  
  let txt = ''; let col = '';
  if (trx.tipo === 'Saldado') {
     if (esPagador) { txt = `Pagaste S/ ${trx.monto.toFixed(2)}`; col = 'text-emerald-600'; } 
     else if (estoyInvolucrado) { txt = `Recibiste S/ ${cuota.toFixed(2)}`; col = 'text-emerald-600'; } 
     else { txt = `No participas`; col = 'text-slate-400'; }
  } else {
     if (esPagador) { txt = `Prestas S/ ${(trx.monto - (estoyInvolucrado ? cuota : 0)).toFixed(2)}`; col = 'text-emerald-600'; } 
     else if (estoyInvolucrado) { txt = `Debes S/ ${cuota.toFixed(2)}`; col = 'text-rose-500'; }
     else { txt = 'No participas'; col = 'text-slate-400'; }
  }

  const esParcial = Boolean(implicadosGrupo && involucrados.length > 0 && implicadosGrupo.length > involucrados.length);
  const nombrePagador = trx.pagadorEmail ? formateador(trx.pagadorEmail) : formateador(miEmail);

  return (
    <div onClick={() => onSelect(trx)} className="p-4 rounded-2xl hover:bg-slate-50 transition-all cursor-pointer flex items-center gap-4 group border border-transparent hover:border-slate-100 relative overflow-hidden active:scale-[0.98]">
       {esParcial && <div className="absolute top-0 right-0 bg-indigo-100 text-indigo-700 text-[8px] font-black px-2 py-0.5 rounded-bl-lg uppercase">Div. Parcial</div>}
       <div className="flex flex-col items-center w-12 shrink-0 relative">
          <span className="text-[10px] text-slate-400 uppercase font-black">{trx.fecha.split(' ')[0]}</span>
          {trx.editado && <span className="text-[8px] mt-0.5" title="Editado">✏️</span>}
          {trx.reciboUrl && <span className="absolute -bottom-1 -right-1 bg-white border border-slate-200 rounded-full h-5 w-5 flex items-center justify-center text-[10px] shadow-sm z-10">📎</span>}
          <div className={`h-12 w-12 rounded-xl mt-1 flex items-center justify-center text-xl shadow-sm transition-colors ${trx.tipo === 'Saldado' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200'}`}>{trx.tipo === 'Saldado' ? '💸' : '🧾'}</div>
       </div>
       <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-800 leading-tight truncate">
            <span className="font-black">{nombrePagador}</span> {trx.tipo === 'Saldado' ? ' registró un pago.' : ' añadió '} {!trx.tipo.includes('Saldado') && <span className="font-bold text-slate-600">"{trx.tienda}"</span>} {trx.tipo !== 'Saldado' && grupo ? ` en ${grupo.nombre}` : ''}
          </p>
          <p className={`text-sm font-black mt-1 ${col}`}>{txt}</p>
       </div>
       <div className="text-right shrink-0">
          <p className="text-[9px] text-slate-400 uppercase font-black mb-0.5 tracking-widest">Total</p>
          <p className="text-sm md:text-base font-black text-slate-900">S/ {trx.monto.toFixed(2)}</p>
       </div>
    </div>
  );
};

// ==========================================
// 2. APLICACIÓN PRINCIPAL
// ==========================================
export default function App() {
  const [usuarioReal, setUsuarioReal] = useState(null); 
  const [cargandoAuth, setCargandoAuth] = useState(true);
  const [cargandoDatos, setCargandoDatos] = useState(true); 
  const [progresoGlobal, setProgresoGlobal] = useState(0); 

  const [modoRegistro, setModoRegistro] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nombreRegistro, setNombreRegistro] = useState('');

  const [vistaActiva, setVistaActiva] = useState('dashboard'); 
  const [grupoActivoId, setGrupoActivoId] = useState(null);
  const [amigoActivoEmail, setAmigoActivoEmail] = useState(null);
  
  const [mostrarFormularioGasto, setMostrarFormularioGasto] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [idGastoEdicion, setIdGastoEdicion] = useState(null);

  const [mostrarFormularioGrupo, setMostrarFormularioGrupo] = useState(false);
  const [mostrarFormularioAmigo, setMostrarFormularioAmigo] = useState(false);
  const [nuevoAmigoEmail, setNuevoAmigoEmail] = useState('');
  const [usuarioNoEncontrado, setUsuarioNoEncontrado] = useState(null);
  
  const [transaccionSeleccionada, setTransaccionSeleccionada] = useState(null);
  const [amigoSeleccionadoModal, setAmigoSeleccionadoModal] = useState(null); 
  const [alertaApp, setAlertaApp] = useState(null);
  
  const [reciboAbierto, setReciboAbierto] = useState(null);
  
  const [mostrarNotificaciones, setMostrarNotificaciones] = useState(false);
  const [mostrarMenuPerfil, setMostrarMenuPerfil] = useState(false);
  const [dropdownGrupoAbierto, setDropdownGrupoAbierto] = useState(false);
  
  const [busquedaGlobal, setBusquedaGlobal] = useState('');
  const [mostrarResultados, setMostrarResultados] = useState(false);
  const [busquedaAmigos, setBusquedaAmigos] = useState(''); 
  
  const [mostrarConfirmacionEliminar, setMostrarConfirmacionEliminar] = useState(false);
  const [mostrarConfirmacionLiquidar, setMostrarConfirmacionLiquidar] = useState(false);
  const [mostrarConfirmacionEliminarAmigo, setMostrarConfirmacionEliminarAmigo] = useState(false);
  const [mostrarConfirmacionEliminarTrx, setMostrarConfirmacionEliminarTrx] = useState(false);
  
  const [pagoPendienteConf, setPagoPendienteConf] = useState(null);

  const [grupos, setGrupos] = useState([]);
  const [invitacionesPendientes, setInvitacionesPendientes] = useState([]); 
  const [historialGlobal, setHistorialGlobal] = useState([]); 
  const [amistades, setAmistades] = useState([]); 
  const [solicitudesAmistad, setSolicitudesAmistad] = useState([]);
  const [alertasGenerales, setAlertasGenerales] = useState([]);
  const [directorioUsuarios, setDirectorioUsuarios] = useState({});

  const [nombreEditado, setNombreEditado] = useState('');
  const [notifPush, setNotifPush] = useState(true);
  const [notifEmail, setNotifEmail] = useState(false);
  
  const [ultimaRevisionNotif, setUltimaRevisionNotif] = useState(0);

  const [inputDesc, setInputDesc] = useState('');
  const [inputMonto, setInputMonto] = useState('');
  const [inputTipo, setInputTipo] = useState('Gasto'); 
  const [inputPagadorEmail, setInputPagadorEmail] = useState(''); 
  const [inputInvolucrados, setInputInvolucrados] = useState([]);
  const [inputGrupoId, setInputGrupoId] = useState('');
  
  const [imagenAdjuntaBase64, setImagenAdjuntaBase64] = useState(null);
  const [nombreArchivoVisible, setNombreArchivoVisible] = useState('');

  const [nuevoGrupoNombre, setNuevoGrupoNombre] = useState('');
  const [invitadosSeleccionados, setInvitadosSeleccionados] = useState([]);
  const [inputNuevoCorreo, setInputNuevoCorreo] = useState('');

  const miNombre = usuarioReal?.displayName || 'Usuario';
  const miEmailLogico = usuarioReal?.email?.toLowerCase() || '';

  const miHistorial = historialGlobal.filter(trx => grupos.some(g => g.id === trx.grupoId));

  const mostrarAlerta = (title, msg, icon = '⚠️', color = 'amber') => {
    setAlertaApp({ title, msg, icon, color });
  };

  const ejecutarConCarga = async (promesaAsincrona) => {
    setProgresoGlobal(15);
    const intervaloCarga = setInterval(() => {
       setProgresoGlobal(prev => prev < 85 ? prev + Math.floor(Math.random() * 15) : prev);
    }, 150);

    try {
       await promesaAsincrona();
    } finally {
       clearInterval(intervaloCarga);
       setProgresoGlobal(100);
       setTimeout(() => setProgresoGlobal(0), 400); 
    }
  };

  useEffect(() => {
    setProgresoGlobal(5);
    const intCarga = setInterval(() => setProgresoGlobal(p => p < 90 ? p + 10 : p), 100);

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      clearInterval(intCarga);
      setProgresoGlobal(100);
      setUsuarioReal(user);
      if(user) {
         setNombreEditado(user.displayName || '');
         setCargandoAuth(false);
         setTimeout(() => setCargandoDatos(false), 800); 
      } else {
         setCargandoAuth(false);
         setCargandoDatos(false);
      }
      setTimeout(() => setProgresoGlobal(0), 500); 
    });
    return () => { unsubAuth(); clearInterval(intCarga); };
  }, []);

  useEffect(() => {
    if (miEmailLogico) {
      const guardado = localStorage.getItem(`notif_${miEmailLogico}`);
      if (guardado) setUltimaRevisionNotif(parseInt(guardado, 10));
    }
  }, [miEmailLogico]);

  useEffect(() => {
    if (!usuarioReal || !usuarioReal.emailVerified) {
      setGrupos([]); setHistorialGlobal([]); setInvitacionesPendientes([]); setGrupoActivoId(null); setAmigoActivoEmail(null); setAmistades([]); setSolicitudesAmistad([]); setAlertasGenerales([]); setDirectorioUsuarios({}); return;
    }
    
    const miEmail = usuarioReal.email.toLowerCase();

    const unsubDiccionario = onSnapshot(collection(db, 'usuarios'), (snapshot) => {
      const mapa = {};
      snapshot.forEach(doc => {
        const data = doc.data();
        if(data.email && data.nombre) {
          mapa[data.email.toLowerCase()] = data.nombre;
        }
      });
      setDirectorioUsuarios(mapa);
    });

    const unsubGrupos = onSnapshot(query(collection(db, 'grupos'), where('miembros', 'array-contains', miEmail)), (snapshot) => {
      setGrupos(snapshot.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.timestamp - a.timestamp));
    });

    const unsubInvitaciones = onSnapshot(query(collection(db, 'grupos'), where('invitados', 'array-contains', miEmail)), (snapshot) => {
      setInvitacionesPendientes(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const unsubHistorial = onSnapshot(query(collection(db, 'transacciones'), orderBy('timestamp', 'desc')), (snapshot) => {
      setHistorialGlobal(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const unsubAmistades = onSnapshot(query(collection(db, 'amistades'), where('usuarios', 'array-contains', miEmail)), (snapshot) => {
       const lista = [];
       snapshot.forEach(d => {
         const elOtro = d.data().usuarios.find(u => u !== miEmail);
         if (elOtro && !lista.some(a => a.email === elOtro)) lista.push({ id: d.id, email: elOtro, timestamp: d.data().timestamp });
       });
       setAmistades(lista.sort((a, b) => b.timestamp - a.timestamp));
    });

    const unsubSolicitudes = onSnapshot(query(collection(db, 'solicitudes_amistad'), where('para', '==', miEmail)), (snapshot) => {
      setSolicitudesAmistad(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const unsubAlertas = onSnapshot(query(collection(db, 'alertas'), where('para', '==', miEmail)), (snapshot) => {
      setAlertasGenerales(snapshot.docs.map(d => ({ id: d.id, ...d.data() })).sort((a,b) => b.timestamp - a.timestamp));
    });

    return () => { unsubGrupos(); unsubInvitaciones(); unsubHistorial(); unsubAmistades(); unsubSolicitudes(); unsubAlertas(); unsubDiccionario(); };
  }, [usuarioReal]);

  const manejarAcceso = async (e) => {
    e.preventDefault();
    if (modoRegistro && !nombreRegistro.trim()) return mostrarAlerta("Faltan Datos", "Por favor, ingresa tu nombre completo.", "👤", "amber");
    
    ejecutarConCarga(async () => {
      try {
        if (modoRegistro) {
          const credencial = await createUserWithEmailAndPassword(auth, email, password);
          await updateProfile(credencial.user, { displayName: nombreRegistro });
          await sendEmailVerification(credencial.user);
          await addDoc(collection(db, 'usuarios'), { uid: credencial.user.uid, email: email.toLowerCase().trim(), nombre: nombreRegistro });
          setUsuarioReal({ ...credencial.user, displayName: nombreRegistro }); 
          setNombreEditado(nombreRegistro);
        } else {
          await signInWithEmailAndPassword(auth, email, password);
        }
      } catch (error) { mostrarAlerta("Error de Acceso", "Verifica tus credenciales e intenta nuevamente.", "🔒", "rose"); }
    });
  };

  const manejarAccesoGoogle = async () => {
    ejecutarConCarga(async () => {
      try {
        const provider = new GoogleAuthProvider();
        const credencial = await signInWithPopup(auth, provider);
        const user = credencial.user;
        const emailLimpio = user.email.toLowerCase().trim();
        const snap = await getDocs(query(collection(db, 'usuarios'), where('email', '==', emailLimpio)));
        
        if (!modoRegistro && snap.empty) {
          await signOut(auth);
          mostrarAlerta("Acceso Denegado", "No tienes una cuenta registrada. Por favor, ve a la opción 'Regístrate' primero.", "🛑", "amber");
          return;
        }
        
        if (modoRegistro && snap.empty) {
          await addDoc(collection(db, 'usuarios'), { uid: user.uid, email: emailLimpio, nombre: user.displayName || 'Usuario' });
        }
      } catch (error) {
        if (error.code !== 'auth/popup-closed-by-user') mostrarAlerta("Error", "Hubo un error al intentar acceder con Google.", "⚠️", "rose");
      }
    });
  };

  const cerrarSesion = async () => {
    ejecutarConCarga(async () => {
      try {
        setGrupos([]); setHistorialGlobal([]); setInvitacionesPendientes([]); setGrupoActivoId(null); setAmigoActivoEmail(null); setVistaActiva('dashboard'); setMostrarNotificaciones(false); setMostrarMenuPerfil(false); setBusquedaGlobal(''); setBusquedaAmigos(''); setMostrarConfirmacionLiquidar(false); setMostrarConfirmacionEliminar(false); setUsuarioNoEncontrado(null); setAmigoSeleccionadoModal(null); setMostrarConfirmacionEliminarAmigo(false); setMostrarConfirmacionEliminarTrx(false); setInvitadosSeleccionados([]); setInputNuevoCorreo(''); setReciboAbierto(null);
        await signOut(auth);
      } catch (error) { console.warn("Sesión cerrada localmente."); }
    });
  };

  const togglePanelNotificaciones = (e) => {
    e.stopPropagation();
    const abriendo = !mostrarNotificaciones;
    setMostrarNotificaciones(abriendo);
    setMostrarMenuPerfil(false);
    setDropdownGrupoAbierto(false);

    if (abriendo) {
       const ahora = Date.now();
       setUltimaRevisionNotif(ahora);
       localStorage.setItem(`notif_${miEmailLogico}`, ahora.toString());
    }
  };

  const aceptarInvitacion = (grupo) => {
    ejecutarConCarga(async () => {
      try { 
        await updateDoc(doc(db, 'grupos', grupo.id), { invitados: arrayRemove(miEmailLogico), miembros: arrayUnion(miEmailLogico) }); 
        if (grupo.creadorEmail && grupo.creadorEmail !== miEmailLogico) {
           if (!amistades.some(a => a.email === grupo.creadorEmail)) {
              await addDoc(collection(db, 'amistades'), { usuarios: [miEmailLogico, grupo.creadorEmail], timestamp: new Date().getTime() });
           }
           await addDoc(collection(db, 'alertas'), { para: grupo.creadorEmail, texto: `${miNombre} se ha unido a tu grupo "${grupo.nombre}".`, tipo: 'exito', timestamp: new Date().getTime() });
        }
      } catch (error) { mostrarAlerta("Error", "No se pudo aceptar la invitación.", "⚠️", "rose"); }
    });
  };

  const rechazarInvitacion = (grupo) => {
    ejecutarConCarga(async () => {
      try { 
        await updateDoc(doc(db, 'grupos', grupo.id), { invitados: arrayRemove(miEmailLogico) }); 
        if (grupo.creadorEmail && grupo.creadorEmail !== miEmailLogico) {
            await addDoc(collection(db, 'alertas'), { para: grupo.creadorEmail, texto: `${miNombre} declinó la invitación al grupo "${grupo.nombre}".`, tipo: 'error', timestamp: new Date().getTime() });
        }
      } catch (error) {}
    });
  };

  const cambiarVista = (vista, idExtra = null) => {
    setVistaActiva(vista);
    if (vista === 'detalleGrupo') { setGrupoActivoId(idExtra); setAmigoActivoEmail(null); } 
    else if (vista === 'detalleAmigo') { setAmigoActivoEmail(idExtra); setGrupoActivoId(null); } 
    else { setGrupoActivoId(null); setAmigoActivoEmail(null); }
    
    if (vista !== 'amigos') setBusquedaAmigos('');
    setMostrarNotificaciones(false); setMostrarMenuPerfil(false); setMostrarResultados(false); setBusquedaGlobal(''); setMostrarConfirmacionLiquidar(false); setMostrarConfirmacionEliminar(false); setUsuarioNoEncontrado(null); setAmigoSeleccionadoModal(null); setMostrarConfirmacionEliminarAmigo(false); setMostrarConfirmacionEliminarTrx(false); setReciboAbierto(null);
  };

  const guardarCambiosPerfil = async () => {
    if(!nombreEditado.trim()) return mostrarAlerta("Faltan datos", "El nombre no puede estar vacío.", "👤", "amber");
    ejecutarConCarga(async () => {
      try {
        await updateProfile(auth.currentUser, { displayName: nombreEditado });
        setUsuarioReal({ ...usuarioReal, displayName: nombreEditado });
        const q = query(collection(db, 'usuarios'), where('email', '==', miEmailLogico));
        const snap = await getDocs(q);
        snap.forEach(async (documento) => { await updateDoc(doc(db, 'usuarios', documento.id), { nombre: nombreEditado }); });
        mostrarAlerta("¡Éxito!", "Perfil actualizado correctamente.", "✅", "emerald");
      } catch (error) { mostrarAlerta("Error", "Hubo un error al actualizar el perfil.", "⚠️", "rose"); }
    });
  };

  const solicitarCambioPassword = async () => {
    ejecutarConCarga(async () => {
      try { await sendPasswordResetEmail(auth, usuarioReal.email); mostrarAlerta("¡Enlace enviado!", "Revisa tu bandeja de entrada para actualizar tu contraseña de forma segura.", "🔒", "emerald"); } catch (error) { mostrarAlerta("Error", "Hubo un error al procesar tu solicitud.", "⚠️", "rose"); }
    });
  };

  const intentarLiquidarGrupo = () => {
    const grupo = grupos.find(g => g.id === grupoActivoId);
    if (!grupo) return;
    if (!grupo.activo) return mostrarAlerta("Aviso", "Este grupo ya está liquidado y cerrado.", "🔒", "amber");

    const balances = calcularBalancesDelGrupo(grupoActivoId);
    const balanceTotal = Object.values(balances).reduce((acc, curr) => acc + Math.abs(curr), 0);

    if (balanceTotal > 0.05) return mostrarAlerta("Acción Denegada", "No puedes liquidar un grupo que tiene deudas pendientes. Todos deben estar en S/ 0.00.", "⚠️", "rose");
    setMostrarConfirmacionLiquidar(true);
  };

  const ejecutarLiquidacionGrupo = async () => {
    ejecutarConCarga(async () => {
      try { await updateDoc(doc(db, 'grupos', grupoActivoId), { activo: false }); setMostrarConfirmacionLiquidar(false); cambiarVista('grupos'); } catch (error) { mostrarAlerta("Error", "Error al liquidar el grupo.", "⚠️", "rose"); }
    });
  };

  const ejecutarEliminacionGrupo = async () => {
    ejecutarConCarga(async () => {
      try {
        const idParaBorrar = grupoActivoId;
        setMostrarConfirmacionEliminar(false); cambiarVista('grupos');
        const snap = await getDocs(query(collection(db, 'transacciones'), where('grupoId', '==', idParaBorrar)));
        
        for (const documento of snap.docs) {
            const data = documento.data();
            if (data.reciboUrl) {
                try {
                    const fileRef = ref(storage, data.reciboUrl);
                    await deleteObject(fileRef);
                } catch (e) { console.error("Error borrando foto huérfana", e); }
            }
            await deleteDoc(doc(db, 'transacciones', documento.id));
        }
        
        await deleteDoc(doc(db, 'grupos', idParaBorrar));
      } catch (error) { mostrarAlerta("Error", "Error al eliminar el grupo.", "⚠️", "rose"); }
    });
  };

  const ejecutarEliminacionAmigo = async () => {
    if (!amigoSeleccionadoModal) return;
    ejecutarConCarga(async () => {
      try { await deleteDoc(doc(db, 'amistades', amigoSeleccionadoModal.id)); setMostrarConfirmacionEliminarAmigo(false); setAmigoSeleccionadoModal(null); } catch (error) { mostrarAlerta("Error", "Error al eliminar el contacto.", "⚠️", "rose"); }
    });
  };

  const eliminarAlerta = async (id) => {
    try { await deleteDoc(doc(db, 'alertas', id)); } catch (e) {}
  };

  const ejecutarEliminacionTransaccion = async () => {
    if (!transaccionSeleccionada) return;
    ejecutarConCarga(async () => {
      try { 
        const nombreGasto = transaccionSeleccionada.tienda;
        const involucrados = transaccionSeleccionada.involucradosEmails || [];
        const pagador = transaccionSeleccionada.pagadorEmail;
        
        if (transaccionSeleccionada.reciboUrl) {
            try {
                const fileRef = ref(storage, transaccionSeleccionada.reciboUrl);
                await deleteObject(fileRef);
            } catch (e) { console.error("Error borrando foto individual", e); }
        }

        const afectados = new Set([...involucrados, pagador]);
        afectados.forEach(async (email) => {
            if (email && email.toLowerCase() !== miEmailLogico) {
                await addDoc(collection(db, 'alertas'), { para: email.toLowerCase(), texto: `${miNombre} eliminó el registro "${nombreGasto}".`, tipo: 'error', timestamp: new Date().getTime() });
            }
        });

        await deleteDoc(doc(db, 'transacciones', transaccionSeleccionada.id)); 
        setMostrarConfirmacionEliminarTrx(false); 
        setTransaccionSeleccionada(null); 
      } catch (error) { mostrarAlerta("Error", "Error al eliminar el gasto.", "⚠️", "rose"); }
    });
  };

  const enviarSolicitudAmistad = async () => {
    if (!nuevoAmigoEmail.trim()) return mostrarAlerta("Faltan datos", "Ingresa un correo electrónico válido.", "✉️", "amber");
    const emailLimpio = nuevoAmigoEmail.toLowerCase().trim();
    if (emailLimpio === miEmailLogico) return mostrarAlerta("Aviso", "No puedes agregarte a ti mismo.", "✋", "amber");
    
    ejecutarConCarga(async () => {
      try {
        const snap = await getDocs(query(collection(db, 'usuarios'), where('email', '==', emailLimpio)));
        if (snap.empty) { setUsuarioNoEncontrado(emailLimpio); return; }
        if (amistades.some(a => a.email === emailLimpio)) { setMostrarFormularioAmigo(false); setNuevoAmigoEmail(''); return mostrarAlerta("Aviso", "Este usuario ya está en tu lista de amigos.", "👥", "amber"); }
        const snapSol = await getDocs(query(collection(db, 'solicitudes_amistad'), where('de', '==', miEmailLogico), where('para', '==', emailLimpio)));
        if (!snapSol.empty) { setMostrarFormularioAmigo(false); setNuevoAmigoEmail(''); return mostrarAlerta("Aviso", "Ya le has enviado una solicitud a este usuario.", "⏳", "amber"); }

        await addDoc(collection(db, 'solicitudes_amistad'), { de: miEmailLogico, deNombre: miNombre, para: emailLimpio, timestamp: new Date().getTime() });
        setMostrarFormularioAmigo(false); setNuevoAmigoEmail(''); setUsuarioNoEncontrado(null);
        mostrarAlerta("¡Enviado!", "Solicitud de amistad enviada con éxito.", "✅", "emerald");
      } catch (error) { mostrarAlerta("Error", "Hubo un error al enviar la solicitud.", "⚠️", "rose"); }
    });
  };

  const procesarSolicitudAmistad = async (solicitud, accion) => {
    ejecutarConCarga(async () => {
      try {
         await deleteDoc(doc(db, 'solicitudes_amistad', solicitud.id)); 
         if (accion === 'aceptar') {
           await addDoc(collection(db, 'amistades'), { usuarios: [solicitud.para, solicitud.de], timestamp: new Date().getTime() });
           await addDoc(collection(db, 'alertas'), { para: solicitud.de, texto: `${miNombre} aceptó tu solicitud de amistad.`, tipo: 'exito', timestamp: new Date().getTime() });
         } else {
           await addDoc(collection(db, 'alertas'), { para: solicitud.de, texto: `${miNombre} rechazó tu solicitud de amistad.`, tipo: 'error', timestamp: new Date().getTime() });
         }
      } catch (e) { mostrarAlerta("Error", "Error procesando la solicitud.", "⚠️", "rose"); }
    });
  };

  const formatearNombre = (nombreCompleto) => {
    if (!nombreCompleto) return 'Usuario';
    const partes = nombreCompleto.trim().split(/\s+/);
    if (partes.length > 1) return `${partes[0]} ${partes[1].charAt(0).toUpperCase()}.`;
    return partes[0];
  };

  const obtenerNombreCorto = (emailParaBuscar) => {
    if (!emailParaBuscar) return 'Usuario';
    const emailLimpio = emailParaBuscar.toLowerCase();
    if (emailLimpio === miEmailLogico && miNombre) return formatearNombre(miNombre);
    const nombreCompletoBD = directorioUsuarios[emailLimpio];
    if (nombreCompletoBD) return formatearNombre(nombreCompletoBD);
    return emailLimpio.split('@')[0]; 
  };

  const calcularBalancesDelGrupo = (grupoId) => {
      const trxs = miHistorial.filter(h => h.grupoId === grupoId);
      const balances = {};
      
      trxs.forEach(trx => {
         const involucrados = trx.involucradosEmails || [];
         const cuota = trx.monto / (involucrados.length > 0 ? involucrados.length : 1);
         const pagador = trx.pagadorEmail ? trx.pagadorEmail.toLowerCase() : null;

         if(pagador) balances[pagador] = (balances[pagador] || 0) + trx.monto;
         
         involucrados.forEach(inv => { 
             const invLimpio = inv.toLowerCase();
             balances[invLimpio] = (balances[invLimpio] || 0) - cuota; 
         });
      });
      return balances;
  };

  const calcularDeudaExacta = (emailDeudor, emailAcreedor, grupoId) => {
    const trxs = miHistorial.filter(h => h.grupoId === grupoId);
    let deuda = 0; 
    trxs.forEach(trx => {
       const involucrados = trx.involucradosEmails || [];
       const cuota = trx.monto / (involucrados.length > 0 ? involucrados.length : 1);
       const pagador = trx.pagadorEmail ? trx.pagadorEmail.toLowerCase() : null;

       const participaDeudor = involucrados.length === 0 || involucrados.includes(emailDeudor);
       const participaAcreedor = involucrados.length === 0 || involucrados.includes(emailAcreedor);

       if (trx.tipo === 'Gasto') {
           if (pagador === emailAcreedor && participaDeudor) deuda += cuota;
           if (pagador === emailDeudor && participaAcreedor) deuda -= cuota;
       } else if (trx.tipo === 'Saldado') {
           const cuotaSaldado = trx.monto / (involucrados.length > 0 ? involucrados.length : 1);
           if (pagador === emailDeudor && participaAcreedor) deuda -= cuotaSaldado;
           if (pagador === emailAcreedor && participaDeudor) deuda += cuotaSaldado;
       }
    });
    return deuda;
  };

  const generarPagosOptimizados = (grupoId) => {
     const balances = calcularBalancesDelGrupo(grupoId);
     
     const deudores = [];
     const acreedores = [];
     
     Object.keys(balances).forEach(email => {
         const bal = balances[email];
         if (bal < -0.01) deudores.push({ email, balance: bal });
         else if (bal > 0.01) acreedores.push({ email, balance: bal });
     });

     const pagos = [];
     
     while (deudores.length > 0 && acreedores.length > 0) {
         deudores.sort((a, b) => a.balance - b.balance); 
         acreedores.sort((a, b) => b.balance - a.balance); 

         let deudor = deudores[0];
         let acreedor = acreedores[0];

         let montoATransferir = Math.min(Math.abs(deudor.balance), acreedor.balance);

         pagos.push({
             deudorEmail: deudor.email,
             acreedorEmail: acreedor.email,
             monto: montoATransferir
         });

         deudor.balance += montoATransferir;
         acreedor.balance -= montoATransferir;

         if (Math.abs(deudor.balance) < 0.01) deudores.shift();
         if (Math.abs(acreedor.balance) < 0.01) acreedores.shift();
     }

     return pagos;
  };

  const liquidarDeudaOptimizada = (pago, nombreGrupo) => {
    ejecutarConCarga(async () => {
      const fechaActual = new Date();
      const pagadorNombre = obtenerNombreCorto(pago.deudorEmail); 
      
      const dataAguardar = {
        grupoId: grupoActivoId, 
        tienda: 'Pago de deuda', 
        persona: pagadorNombre,
        pagadorEmail: pago.deudorEmail, 
        involucradosEmails: [pago.acreedorEmail], 
        tipo: 'Saldado', 
        monto: parseFloat(pago.monto), 
        division: 'n/a',
        hora: fechaActual.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' }),
        fecha: fechaActual.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' }),
        timestamp: fechaActual.getTime()
      };

      try {
        await addDoc(collection(db, 'transacciones'), dataAguardar);
        
        if (pago.acreedorEmail !== miEmailLogico) {
            await addDoc(collection(db, 'alertas'), { 
                para: pago.acreedorEmail, 
                texto: `${pagadorNombre} te ha pagado S/ ${pago.monto.toFixed(2)} en "${nombreGrupo}".`, 
                tipo: 'exito', 
                timestamp: new Date().getTime() 
            });
        }
        mostrarAlerta("Pago Registrado", "La transferencia se ha guardado y los balances se han actualizado.", "✅", "emerald");
      } catch (error) { mostrarAlerta("Error", "Hubo un error al registrar el pago.", "⚠️", "rose"); }
    });
  };

  const exportarExcel = () => {
      const grupo = grupos.find(g => g.id === grupoActivoId);
      if (!grupo) return;
      const trxs = miHistorial.filter(h => h.grupoId === grupo.id);
      const balances = calcularBalancesDelGrupo(grupo.id);
      const pagos = generarPagosOptimizados(grupo.id);
      
      let csv = '\uFEFF'; 
      csv += `REPORTE EJECUTIVO: ${grupo.nombre.toUpperCase()}\n`;
      csv += `Fecha de generación: ${new Date().toLocaleDateString('es-PE')}\n\n`;
      
      csv += `1. ESTADO DE BALANCES\n`;
      csv += `Nombre,Balance Final\n`;
      Object.keys(balances).forEach(email => {
         csv += `${obtenerNombreCorto(email)},${balances[email].toFixed(2)}\n`;
      });
      
      csv += `\n2. LIQUIDACIÓN (QUIÉN PAGA A QUIÉN)\n`;
      if(pagos.length === 0) csv += `Todo está saldado.\n`;
      else {
         csv += `Deudor,Acreedor,Monto\n`;
         pagos.forEach(p => {
            csv += `${obtenerNombreCorto(p.deudorEmail)},${obtenerNombreCorto(p.acreedorEmail)},${p.monto.toFixed(2)}\n`;
         });
      }
      
      csv += `\n3. HISTORIAL DE MOVIMIENTOS\n`;
      csv += `Fecha,Concepto,Pagador,Monto,Tipo\n`;
      trxs.forEach(t => {
         csv += `${t.fecha},${t.tienda},${t.pagadorEmail ? obtenerNombreCorto(t.pagadorEmail) : t.persona},${t.monto.toFixed(2)},${t.tipo}\n`;
      });
      
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Reporte_${grupo.nombre.replace(/\s+/g, '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  };

  // 📥 FUNCIÓN PARA FORZAR LA DESCARGA DEL RECIBO DESDE FIREBASE (CORREGIDA)
  const descargarRecibo = async (url) => {
    let intCarga; 
    try {
      setProgresoGlobal(10);
      intCarga = setInterval(() => setProgresoGlobal(p => p < 90 ? p + 15 : p), 200);
      
      const response = await fetch(url);
      
      if (!response.ok) throw new Error("El servidor bloqueó la descarga (CORS o Red)");

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Recibo_CuentasClaras_${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      
      clearInterval(intCarga);
      setProgresoGlobal(100);
      setTimeout(() => setProgresoGlobal(0), 400);
      
    } catch (error) {
      console.error("Error forzando descarga, usando método alternativo:", error);
      
      clearInterval(intCarga);
      setProgresoGlobal(0);
      
      window.open(url, '_blank'); 
    }
  };

  let totalPorCobrar = 0;
  let totalPorPagar = 0;
  const gruposActivos = grupos.filter(g => g.activo);
  const amigosMap = {};

  amistades.forEach(a => {
    amigosMap[a.email] = { id: a.id, email: a.email, timestamp: a.timestamp, cantidadGrupos: 0, gruposCompartidos: [] };
  });

  gruposActivos.forEach(grupo => {
    const balancesGrupo = calcularBalancesDelGrupo(grupo.id);
    const miSaldoNetoEnGrupo = balancesGrupo[miEmailLogico] || 0;
    
    const implicadosGrupo = [...(grupo.miembros || []), ...(grupo.invitados || [])];
    const otrosEnGrupo = implicadosGrupo.filter(m => m.toLowerCase() !== miEmailLogico);
    otrosEnGrupo.forEach(otro => {
       const otroMinuscula = otro.toLowerCase();
       if(amigosMap[otroMinuscula]) {
         if (!amigosMap[otroMinuscula].gruposCompartidos.some(g => g.id === grupo.id)) {
             amigosMap[otroMinuscula].cantidadGrupos += 1;
             amigosMap[otroMinuscula].gruposCompartidos.push(grupo);
         }
       }
    });

    if (miSaldoNetoEnGrupo > 0.01) totalPorCobrar += miSaldoNetoEnGrupo;
    if (miSaldoNetoEnGrupo < -0.01) totalPorPagar += Math.abs(miSaldoNetoEnGrupo); 
  });

  const balanceNeto = totalPorCobrar - totalPorPagar;
  const listaDirectorioAmigos = Object.values(amigosMap).sort((a,b) => b.timestamp - a.timestamp);

  const buscarGlobalmente = () => {
     if(!busquedaGlobal.trim()) return { grupos: [], amigos: [] };
     const queryLower = busquedaGlobal.toLowerCase();
     return { 
       grupos: grupos.filter(g => g.nombre.toLowerCase().includes(queryLower)), 
       amigos: listaDirectorioAmigos.filter(a => a.email.toLowerCase().includes(queryLower) || obtenerNombreCorto(a.email).toLowerCase().includes(queryLower)) 
     };
  };
  const resultadosBusqueda = buscarGlobalmente();

  const compartirApp = async () => {
    const url = window.location.origin; const texto = `¡Únete a CuentasClaras para dividir gastos fácilmente! 💸`;
    if (navigator.share) { try { await navigator.share({ title: 'CuentasClaras', text: texto, url: url }); } catch (err) {} } 
    else { navigator.clipboard.writeText(`${texto} ${url}`); mostrarAlerta("¡Copiado!", "El enlace se ha copiado al portapapeles.", "📋", "emerald"); }
  };

  const gastosOtrosRecientes = miHistorial.filter(trx => trx.pagadorEmail !== miEmailLogico).slice(0, 5);
  
  const esNuevaNotif = (timestamp) => timestamp > ultimaRevisionNotif;
  const nuevasNotificacionesCount = 
     solicitudesAmistad.filter(x => esNuevaNotif(x.timestamp)).length +
     invitacionesPendientes.filter(x => esNuevaNotif(x.timestamp)).length +
     alertasGenerales.filter(x => esNuevaNotif(x.timestamp)).length +
     gastosOtrosRecientes.filter(x => esNuevaNotif(x.timestamp)).length;

  const totalNotificaciones = 
     solicitudesAmistad.length +
     invitacionesPendientes.length +
     alertasGenerales.length +
     gastosOtrosRecientes.length;

  const abrirFormulario = (idPreseleccionado = null, trxAEditar = null) => {
    if (gruposActivos.length === 0) return mostrarAlerta('Sin grupos', 'Crea un grupo activo primero para poder añadir gastos.', '📁', 'amber');
    
    setImagenAdjuntaBase64(null);
    setNombreArchivoVisible('');

    if (trxAEditar) {
      setModoEdicion(true);
      setIdGastoEdicion(trxAEditar.id);
      setInputGrupoId(trxAEditar.grupoId);
      setInputPagadorEmail(trxAEditar.pagadorEmail);
      setInputTipo(trxAEditar.tipo); 
      setInputDesc(trxAEditar.tienda);
      setInputMonto(trxAEditar.monto);
      setInputInvolucrados(trxAEditar.involucradosEmails || []);
    } else {
      setModoEdicion(false);
      setIdGastoEdicion(null);
      const targetGrupoId = idPreseleccionado || (grupoActivoId || gruposActivos[0].id);
      const targetGrupo = gruposActivos.find(g => g.id === targetGrupoId);
      
      setInputGrupoId(targetGrupoId);
      setInputPagadorEmail(miEmailLogico); 
      setInputTipo('Gasto'); 
      setInputDesc(''); 
      setInputMonto('');
      if(targetGrupo) setInputInvolucrados([...(targetGrupo.miembros || []), ...(targetGrupo.invitados || [])]);
    }
    setTransaccionSeleccionada(null);
    setDropdownGrupoAbierto(false); 
    setMostrarFormularioGasto(true);
  };

  const toggleInvolucrado = (email) => {
    const correoLimpio = email.toLowerCase();
    
    if (inputTipo === 'Saldado') {
       if (inputInvolucrados.includes(correoLimpio)) { 
           setInputInvolucrados([]); 
           setInputMonto(''); 
       } else {
           setInputInvolucrados([correoLimpio]); 
           const pagadorActual = inputPagadorEmail.toLowerCase();
           const deudaExacta = calcularDeudaExacta(pagadorActual, correoLimpio, inputGrupoId);
           
           if (deudaExacta > 0.01) {
               setInputMonto(deudaExacta.toFixed(2));
           } else {
               setInputMonto('');
           }
       }
    } else {
       if (inputInvolucrados.includes(correoLimpio)) {
           setInputInvolucrados(inputInvolucrados.filter(e => e !== correoLimpio));
       } else {
           setInputInvolucrados([...inputInvolucrados, correoLimpio]);
       }
    }
  };

  const procesarAdjunto = (evento) => {
    const archivo = evento.target.files[0];
    if (!archivo) return;
    
    if (!archivo.type.startsWith('image/')) return mostrarAlerta("Archivo inválido", "Por favor sube solo imágenes (JPG, PNG).", "🚫", "rose");
    if (archivo.size > 5 * 1024 * 1024) return mostrarAlerta("Archivo muy pesado", "La imagen excede los 5MB permitidos.", "⚖️", "amber");

    setNombreArchivoVisible("Comprimiendo recibo...");

    const reader = new FileReader();
    reader.readAsDataURL(archivo);
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 800; 
        const scaleSize = MAX_WIDTH / img.width;
        
        if (scaleSize < 1) {
            canvas.width = MAX_WIDTH;
            canvas.height = img.height * scaleSize;
        } else {
            canvas.width = img.width;
            canvas.height = img.height;
        }
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        const base64Comprimido = canvas.toDataURL('image/jpeg', 0.6);
        setImagenAdjuntaBase64(base64Comprimido);
        setNombreArchivoVisible(archivo.name);
      };
    };
  };

  const guardarNuevoGasto = async () => {
    if (!inputDesc.trim() || Number(inputMonto) <= 0) return mostrarAlerta("Faltan datos", "Ingresa una descripción y un monto válido.", "📝", "amber");
    if (inputInvolucrados.length === 0) return mostrarAlerta("Faltan datos", "Selecciona con quién compartes el gasto.", "👥", "amber");
    
    ejecutarConCarga(async () => {
      const pagadorNombre = obtenerNombreCorto(inputPagadorEmail); 
      const fechaActual = new Date();
      let reciboUrlGenerada = null;

      if (imagenAdjuntaBase64 && !modoEdicion) {
         try {
            const hashAleatorio = Math.random().toString(36).substring(2, 10);
            const rutaAlmacenamiento = `recibos/${inputGrupoId}/${fechaActual.getTime()}_${hashAleatorio}.jpg`;
            const referenciaStorage = ref(storage, rutaAlmacenamiento);
            
            await uploadString(referenciaStorage, imagenAdjuntaBase64, 'data_url');
            reciboUrlGenerada = await getDownloadURL(referenciaStorage);
         } catch (error) {
            console.error("Error subiendo archivo:", error);
         }
      }
      
      const dataAguardar = {
        grupoId: inputGrupoId, tienda: inputDesc, persona: pagadorNombre,
        pagadorEmail: inputPagadorEmail, involucradosEmails: inputInvolucrados,
        tipo: inputTipo, monto: parseFloat(inputMonto), division: inputTipo === 'Gasto' ? 'partes_iguales' : 'n/a',
        hora: fechaActual.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' }),
        fecha: fechaActual.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' }),
        timestamp: fechaActual.getTime()
      };

      if (reciboUrlGenerada) dataAguardar.reciboUrl = reciboUrlGenerada;

      try {
        if (modoEdicion) {
           await updateDoc(doc(db, 'transacciones', idGastoEdicion), { ...dataAguardar, editado: true });
           const afectados = new Set([...inputInvolucrados, inputPagadorEmail]);
           afectados.forEach(async (email) => {
               if (email && email.toLowerCase() !== miEmailLogico) {
                   await addDoc(collection(db, 'alertas'), { para: email.toLowerCase(), texto: `${miNombre} editó el registro "${inputDesc}".`, tipo: 'info', timestamp: new Date().getTime() });
               }
           });
        } else {
           await addDoc(collection(db, 'transacciones'), dataAguardar);
        }
        setMostrarFormularioGasto(false); setInputDesc(''); setInputMonto(''); setModoEdicion(false); setIdGastoEdicion(null); setImagenAdjuntaBase64(null); setNombreArchivoVisible('');
      } catch (error) { mostrarAlerta("Error", "Hubo un error al procesar el gasto.", "⚠️", "rose"); }
    });
  };

  const toggleInvitado = (emailLimpio) => {
     if (invitadosSeleccionados.includes(emailLimpio)) {
         setInvitadosSeleccionados(invitadosSeleccionados.filter(e => e !== emailLimpio));
     } else {
         setInvitadosSeleccionados([...invitadosSeleccionados, emailLimpio]);
     }
  };

  const agregarCorreoManual = () => {
     const correo = inputNuevoCorreo.trim().toLowerCase();
     if (!correo) return;
     if (correo === miEmailLogico) return mostrarAlerta("Aviso", "Tú ya estás incluido en el grupo por defecto.", "👤", "amber");
     
     const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
     if (!regex.test(correo)) return mostrarAlerta("Aviso", "Ingresa un correo electrónico válido.", "✉️", "amber");

     if (invitadosSeleccionados.includes(correo)) {
        setInputNuevoCorreo('');
        return;
     }
     setInvitadosSeleccionados([...invitadosSeleccionados, correo]);
     setInputNuevoCorreo('');
  };

  const guardarNuevoGrupo = async () => {
    if (!nuevoGrupoNombre.trim() || invitadosSeleccionados.length === 0) return mostrarAlerta("Faltan datos", "Ingresa un nombre y selecciona al menos un invitado.", "📝", "amber");
    
    ejecutarConCarga(async () => {
      const correosLimpios = []; const correosNoRegistrados = [];

      for (let c of invitadosSeleccionados) {
        let limpio = c.toLowerCase().trim();
        if (limpio && limpio !== miEmailLogico) {
          const snap = await getDocs(query(collection(db, 'usuarios'), where('email', '==', limpio)));
          if (snap.empty) correosNoRegistrados.push(limpio); else correosLimpios.push(limpio);
        }
      }

      if (correosNoRegistrados.length > 0) { setUsuarioNoEncontrado(correosNoRegistrados.join(', ')); return; }
      if (correosLimpios.length === 0) return mostrarAlerta("Faltan datos", "No hay invitados válidos para crear el grupo.", "✉️", "amber");

      try {
        for (let emailAmigo of correosLimpios) {
          if (!amistades.some(a => a.email === emailAmigo)) {
            await addDoc(collection(db, 'amistades'), { usuarios: [miEmailLogico, emailAmigo], timestamp: new Date().getTime() });
          }
        }

        await addDoc(collection(db, 'grupos'), {
          nombre: nuevoGrupoNombre, tipo: 'Hogar', icono: '📁', activo: true, moneda: 'S/',
          miembros: [miEmailLogico], invitados: correosLimpios, creador: miNombre, creadorEmail: miEmailLogico, timestamp: new Date().getTime()
        });
        
        setMostrarFormularioGrupo(false); setNuevoGrupoNombre(''); setInvitadosSeleccionados([]); setInputNuevoCorreo(''); setUsuarioNoEncontrado(null); cambiarVista('grupos');
      } catch (error) { mostrarAlerta("Error", "Hubo un error al crear el grupo.", "⚠️", "rose"); }
    });
  };

  // 🛡️ CORRECCIÓN MÓVIL: Removidas las clases "hidden md:block" y "hidden md:flex" de los botones
  const SidebarItem = ({ icono, texto, vista, extraClass = "" }) => {
    const activo = vistaActiva === vista && !grupoActivoId;
    return (
      <button onClick={() => cambiarVista(vista)} className={`w-full flex items-center gap-4 px-5 py-3.5 rounded-xl font-bold transition-all ${activo ? 'bg-emerald-500/10 text-emerald-400 border-r-4 border-emerald-400' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'} ${extraClass} print:hidden`}>
        <span className="text-xl">{icono}</span><span className="text-sm tracking-wide">{texto}</span>
      </button>
    );
  };

  if (cargandoAuth || cargandoDatos) return <DashboardSkeleton />;

  if (!usuarioReal) return (
    <div className="h-screen w-full bg-[#F8FAFC] flex items-center justify-center p-6 font-sans relative">
      <div className={`fixed top-0 left-0 w-full h-1.5 z-[9999] transition-opacity duration-300 ${progresoGlobal > 0 ? 'opacity-100' : 'opacity-0'} print:hidden`}>
         <div className="h-full bg-emerald-500 transition-all duration-200 ease-out shadow-[0_0_12px_rgba(16,185,129,0.9)] relative" style={{ width: `${progresoGlobal}%` }}>
           <div className="absolute top-0 right-0 h-full w-20 bg-gradient-to-r from-transparent to-white opacity-50"></div>
         </div>
      </div>

      <div className="bg-white p-10 rounded-[32px] shadow-xl shadow-slate-200/50 w-full max-w-md border border-slate-100 animate-in fade-in zoom-in-95 duration-500">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="h-12 w-12 rounded-[16px] bg-slate-900 flex items-center justify-center text-white font-black shadow-lg text-2xl">C</div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight italic">CuentasClaras</h1>
        </div>
        
        <button type="button" onClick={manejarAccesoGoogle} className="w-full flex items-center justify-center gap-3 bg-white border border-slate-200 text-slate-700 font-black py-4 rounded-2xl text-xs uppercase tracking-widest hover:bg-slate-50 transition-colors shadow-sm mb-4 active:scale-95">
          <svg className="h-5 w-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 15.02 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Continuar con Google
        </button>

        <div className="flex items-center gap-3 my-6"><hr className="flex-1 border-slate-200"/><span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">o con correo</span><hr className="flex-1 border-slate-200"/></div>

        <form onSubmit={manejarAcceso} className="space-y-4">
          {modoRegistro && <input type="text" value={nombreRegistro} onChange={(e)=>setNombreRegistro(e.target.value)} placeholder="Nombre completo" className="w-full p-4 bg-slate-50 rounded-2xl text-sm font-bold border border-slate-200 outline-none focus:border-emerald-500" />}
          <input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="Correo electrónico" className="w-full p-4 bg-slate-50 rounded-2xl text-sm font-bold border border-slate-200 outline-none focus:border-emerald-500" required />
          <input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} placeholder="Contraseña" className="w-full p-4 bg-slate-50 rounded-2xl text-sm font-bold border border-slate-200 outline-none focus:border-emerald-500" required minLength="6" />
          <button type="submit" className="w-full bg-slate-900 text-white font-black py-4 rounded-2xl text-sm uppercase tracking-widest hover:bg-slate-800 shadow-lg mt-2 active:scale-95 transition-transform">{modoRegistro ? 'Crear cuenta' : 'Ingresar'}</button>
        </form>
        <div className="mt-8 text-center border-t border-slate-100 pt-6"><button onClick={() => setModoRegistro(!modoRegistro)} className="text-slate-500 font-bold text-xs hover:text-emerald-600 uppercase tracking-widest">{modoRegistro ? 'Ya tengo cuenta. Iniciar sesión' : '¿No tienes cuenta? Regístrate'}</button></div>
      </div>
      <ModalAlerta config={alertaApp} onClose={() => setAlertaApp(null)} />
    </div>
  );

  if (usuarioReal && !usuarioReal.emailVerified) return (
    <div className="h-screen w-full bg-[#F8FAFC] flex items-center justify-center p-6 font-sans">
       <div className="bg-white p-10 rounded-[32px] shadow-xl w-full max-w-md text-center border border-slate-100">
         <div className="text-6xl mb-6">✉️</div>
         <h2 className="text-2xl font-black mb-2 tracking-tight">Verifica tu correo</h2>
         <p className="text-sm text-slate-500 mb-8 font-medium">Enlace enviado a <br/><span className="font-bold text-slate-800">{usuarioReal.email}</span></p>
         <button onClick={()=>window.location.reload()} className="w-full bg-emerald-600 text-white font-black py-4 rounded-2xl mb-4 shadow-lg shadow-emerald-600/30 active:scale-95 transition-transform">Ya lo verifiqué</button>
         <button onClick={cerrarSesion} className="text-slate-400 font-bold text-xs uppercase hover:text-rose-500 tracking-widest">Cancelar y Salir</button>
       </div>
       <ModalAlerta config={alertaApp} onClose={() => setAlertaApp(null)} />
    </div>
  );

  const renderDashboard = () => (
    <div className="animate-in fade-in duration-300 pb-24 md:pb-10">
      <div className="mb-8"><h2 className="text-3xl font-black text-slate-900 tracking-tight">Panel Principal</h2><p className="text-sm text-slate-500 mt-1 font-medium">El estado general de tus finanzas en todos los grupos.</p></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        <div className="bg-white p-6 rounded-[24px] border shadow-sm"><p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-slate-300"></span> Balance Neto</p><h3 className={`text-4xl font-black tracking-tighter ${balanceNeto >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>S/ {Math.abs(balanceNeto).toFixed(2)}</h3></div>
        <div className="bg-white p-6 rounded-[24px] border shadow-sm"><p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-400"></span> Por cobrar</p><h3 className="text-4xl font-black tracking-tighter text-slate-900">S/ {totalPorCobrar.toFixed(2)}</h3></div>
        <div className="bg-white p-6 rounded-[24px] border shadow-sm"><p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-rose-400"></span> Por pagar</p><h3 className="text-4xl font-black tracking-tighter text-slate-900">S/ {totalPorPagar.toFixed(2)}</h3></div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white rounded-[32px] border p-2">
          <div className="p-5 flex justify-between items-center border-b border-slate-50"><h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Última Actividad</h4><button onClick={()=>cambiarVista('actividad')} className="text-[10px] font-black uppercase tracking-widest text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-lg active:scale-95 transition-transform">Ver todo</button></div>
          <div className="flex-1 p-2 space-y-1">
            {miHistorial.length === 0 ? <div className="p-12 text-center text-sm font-medium text-slate-400">Aún no hay transacciones registradas.</div> : miHistorial.slice(0, 6).map(trx => {
                const grupo = grupos.find(g => g.id === trx.grupoId);
                const implicadosGrupo = grupo ? [...(grupo.miembros||[]), ...(grupo.invitados||[])] : [];
                return <TransactionItem key={trx.id} trx={trx} grupo={grupo} miEmail={miEmailLogico} miNombre={miNombre} onSelect={setTransaccionSeleccionada} implicadosGrupo={implicadosGrupo} formateador={obtenerNombreCorto} /> 
            })}
          </div>
        </div>
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-slate-900 rounded-[32px] shadow-xl p-8 relative overflow-hidden text-white"><div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-white/5 blur-2xl"></div><h4 className="text-xl font-black mb-2 tracking-tight">Acciones Rápidas</h4><p className="text-sm text-slate-400 font-medium mb-6">Gestiona tus espacios y deudas al instante.</p>
            <button onClick={() => setMostrarFormularioGrupo(true)} className="w-full mb-3 flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white text-xs font-black py-4 rounded-xl transition-colors uppercase tracking-widest backdrop-blur-sm border border-white/10 active:scale-95">📁 Crear Grupo</button>
            <button onClick={() => setMostrarFormularioAmigo(true)} className="w-full mb-3 flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white text-xs font-black py-4 rounded-xl transition-colors uppercase tracking-widest backdrop-blur-sm border border-white/10 active:scale-95">👥 Añadir Amigo</button>
            <button onClick={() => abrirFormulario()} className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-black py-4 rounded-xl transition-colors uppercase tracking-widest shadow-lg shadow-emerald-500/20 mt-4 active:scale-95">🧾 Añadir Gasto</button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderGrupos = () => {
    const filtrados = grupos.filter(g => g.nombre.toLowerCase().includes(busquedaGlobal.toLowerCase()));
    const act = filtrados.filter(g => g.activo); 
    const inact = filtrados.filter(g => !g.activo);

    return (
      <div className="animate-in fade-in duration-300 pb-24 md:pb-10">
        <div className="flex justify-between items-start md:items-center mb-6 md:mb-8 gap-3">
          <div className="flex-1 min-w-0"><h2 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight truncate">Mis Grupos</h2><p className="text-xs md:text-sm text-slate-500 mt-1 font-medium truncate">Tus espacios compartidos e históricos.</p></div>
          {/* 🛡️ CORRECCIÓN MÓVIL: Botón siempre visible */}
          <button onClick={() => setMostrarFormularioGrupo(true)} className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 md:px-5 md:py-3 rounded-xl text-[10px] md:text-xs font-black uppercase transition-all shadow-sm shrink-0 whitespace-nowrap active:scale-95">+ Nuevo Grupo</button>
        </div>

        <div className="lg:hidden mb-6"><div className="flex items-center bg-white border border-slate-200 rounded-2xl px-4 py-3 shadow-sm focus-within:border-emerald-400 transition-colors"><span className="text-slate-400">🔍</span><input type="text" value={busquedaGlobal} onChange={(e) => setBusquedaGlobal(e.target.value)} placeholder="Buscar grupo..." className="bg-transparent border-none outline-none ml-3 text-sm w-full text-slate-700 font-bold" />{busquedaGlobal && <button onClick={() => setBusquedaGlobal('')} className="text-slate-400 font-bold ml-2">×</button>}</div></div>
        
        {filtrados.length === 0 ? (
           <div className="bg-white border border-slate-200 rounded-[32px] p-16 text-center shadow-sm">
              <span className="text-6xl opacity-50">📁</span>
              <h3 className="text-2xl font-black text-slate-800 mt-6 tracking-tight">No se encontraron grupos</h3>
              {!busquedaGlobal && <button onClick={() => setMostrarFormularioGrupo(true)} className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest mt-6 transition-transform shadow-sm active:scale-95">Crear Grupo</button>}
           </div>
        ) : (
          <div className="space-y-10">
            {act.length > 0 && (
              <div><h3 className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-4 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-500"></span> Grupos Activos</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {act.map(g => {
                    const balancesGrupo = calcularBalancesDelGrupo(g.id);
                    const sal = balancesGrupo[miEmailLogico] || 0;
                    const totalMiembros = (g.miembros || []).length + (g.invitados || []).length;

                    return (
                      <div key={g.id} onClick={() => cambiarVista('detalleGrupo', g.id)} className="bg-white p-6 rounded-[24px] border border-slate-200 shadow-sm hover:border-emerald-400 hover:shadow-md transition-all cursor-pointer group flex flex-col active:scale-[0.98]">
                        <div className="flex items-center gap-4 mb-6"><div className="h-14 w-14 bg-slate-50 rounded-2xl flex items-center justify-center text-2xl border border-slate-100 group-hover:bg-emerald-50 transition-colors">{g.icono}</div><div><h3 className="text-lg font-black text-slate-800 leading-tight">{g.nombre}</h3><p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">{totalMiembros} Integrantes</p></div></div>
                        <div className="mt-auto pt-4 border-t border-slate-100 flex justify-between items-end"><div><p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-1">Tu Balance</p><p className={`text-2xl font-black tracking-tighter ${sal > 0.01 ? 'text-emerald-600' : sal < -0.01 ? 'text-rose-500' : 'text-slate-800'}`}>{Math.abs(sal) < 0.01 ? 'Saldado' : `${sal > 0 ? '+' : ''} S/ ${Math.abs(sal).toFixed(2)}`}</p></div><span className="h-8 w-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-300 group-hover:bg-emerald-100 group-hover:text-emerald-600 transition-colors">→</span></div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
            {inact.length > 0 && (
              <div><h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-slate-300"></span> Archivo Histórico (Liquidados)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {inact.map(g => {
                    const totalMiembros = (g.miembros || []).length + (g.invitados || []).length;
                    return (
                      <div key={g.id} onClick={() => cambiarVista('detalleGrupo', g.id)} className="bg-slate-50 p-6 rounded-[24px] border border-slate-200 opacity-75 hover:opacity-100 transition-all cursor-pointer group flex flex-col grayscale hover:grayscale-0 active:scale-[0.98]">
                        <div className="flex items-center gap-4 mb-6"><div className="h-14 w-14 bg-slate-200 rounded-2xl flex items-center justify-center text-2xl border border-slate-300 transition-colors">{g.icono}</div><div><h3 className="text-lg font-black text-slate-800 leading-tight">{g.nombre}</h3><p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">{totalMiembros} Integrantes</p></div></div>
                        <div className="mt-auto pt-4 border-t border-slate-200 flex justify-between items-end"><div><p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mb-1">Balance Final</p><p className="text-2xl font-black tracking-tighter text-slate-500">Saldado</p></div><span className="text-[10px] font-black uppercase text-slate-400 border border-slate-300 px-2 py-1 rounded-md">Cerrado</span></div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderDetalleGrupo = () => {
    const grupo = grupos.find(g => g.id === grupoActivoId); if (!grupo) return null;
    const trxs = miHistorial.filter(h => h.grupoId === grupo.id);
    const implicadosGrupo = [...(grupo.miembros || []), ...(grupo.invitados || [])];
    
    const balancesGrupo = calcularBalancesDelGrupo(grupo.id);
    const miSaldoG = balancesGrupo[miEmailLogico] || 0;
    const pagosOptimizados = generarPagosOptimizados(grupo.id);
    const misPagosPendientes = pagosOptimizados.filter(p => p.deudorEmail === miEmailLogico);

    const trxsFiltradas = trxs.filter(t => t.tienda.toLowerCase().includes(busquedaGlobal.toLowerCase()));

    // 📄 PDF REPORT (Solo visible cuando se llama a window.print())
    const totalGastadoGrupo = trxs.filter(t => t.tipo === 'Gasto').reduce((acc, curr) => acc + curr.monto, 0);

    return (
      <div className="animate-in fade-in duration-300 max-w-5xl mx-auto pb-24 md:pb-10">
        
        {/* 📄 ESTRUCTURA DEL PDF (Oculto en pantalla, visible al imprimir) */}
        <div className="hidden print:block w-full bg-white text-slate-900 font-sans p-8 absolute top-0 left-0 z-[99999]">
            <div className="flex justify-between items-end border-b-2 border-slate-900 pb-4 mb-8">
               <div>
                 <h1 className="text-4xl font-black italic tracking-tight">CuentasClaras</h1>
                 <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">Reporte Ejecutivo de Gastos</p>
               </div>
               <div className="text-right">
                 <h2 className="text-2xl font-black text-slate-800">{grupo.nombre}</h2>
                 <p className="text-xs text-slate-500 font-medium">Generado el: {new Date().toLocaleDateString('es-PE')} por {miNombre}</p>
               </div>
            </div>

            <div className="grid grid-cols-2 gap-8 mb-8">
               <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200">
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Inversión Total del Grupo</p>
                  <p className="text-3xl font-black text-slate-900">S/ {totalGastadoGrupo.toFixed(2)}</p>
               </div>
               <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200">
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Estado de Liquidación</p>
                  <p className={`text-3xl font-black ${pagosOptimizados.length === 0 ? 'text-emerald-600' : 'text-amber-600'}`}>{pagosOptimizados.length === 0 ? '100% Saldado' : 'Deudas Pendientes'}</p>
               </div>
            </div>

            <div className="mb-8">
               <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest border-b border-slate-200 pb-2 mb-4">1. Estado de Balances Individuales</h3>
               <table className="w-full text-sm text-left">
                  <thead><tr className="text-[10px] uppercase text-slate-500 bg-slate-50"><th className="py-2 px-3">Participante</th><th className="py-2 px-3 text-right">Balance Neto</th><th className="py-2 px-3 text-right">Estado</th></tr></thead>
                  <tbody>
                     {implicadosGrupo.map(email => {
                        const bal = balancesGrupo[email.toLowerCase()] || 0;
                        return (
                           <tr key={email} className="border-b border-slate-100">
                              <td className="py-2 px-3 font-bold text-slate-800">{obtenerNombreCorto(email)}</td>
                              <td className="py-2 px-3 text-right font-black">S/ {Math.abs(bal).toFixed(2)}</td>
                              <td className={`py-2 px-3 text-right font-bold text-[10px] uppercase ${bal > 0.01 ? 'text-emerald-600' : bal < -0.01 ? 'text-rose-600' : 'text-slate-400'}`}>{bal > 0.01 ? 'A Favor' : bal < -0.01 ? 'En Deuda' : 'Saldado'}</td>
                           </tr>
                        )
                     })}
                  </tbody>
               </table>
            </div>

            <div className="mb-8">
               <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest border-b border-slate-200 pb-2 mb-4">2. Plan de Pagos Optimizado (Minimize Cash Flow)</h3>
               {pagosOptimizados.length === 0 ? (
                  <p className="text-sm text-emerald-600 font-bold italic">No hay transferencias requeridas. El grupo está saldado.</p>
               ) : (
                  <table className="w-full text-sm text-left">
                     <thead><tr className="text-[10px] uppercase text-slate-500 bg-slate-50"><th className="py-2 px-3">Deudor (Debe pagar)</th><th className="py-2 px-3">Acreedor (Debe recibir)</th><th className="py-2 px-3 text-right">Monto a Transferir</th></tr></thead>
                     <tbody>
                        {pagosOptimizados.map((p, i) => (
                           <tr key={i} className="border-b border-slate-100">
                              <td className="py-2 px-3 font-bold text-rose-600">{obtenerNombreCorto(p.deudorEmail)}</td>
                              <td className="py-2 px-3 font-bold text-emerald-600">{obtenerNombreCorto(p.acreedorEmail)}</td>
                              <td className="py-2 px-3 text-right font-black text-slate-900">S/ {p.monto.toFixed(2)}</td>
                           </tr>
                        ))}
                     </tbody>
                  </table>
               )}
            </div>

            <div>
               <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest border-b border-slate-200 pb-2 mb-4">3. Libro Mayor (Historial Detallado)</h3>
               <table className="w-full text-xs text-left">
                  <thead><tr className="text-[9px] uppercase text-slate-500 bg-slate-50"><th className="py-2 px-3">Fecha</th><th className="py-2 px-3">Concepto</th><th className="py-2 px-3">Pagador</th><th className="py-2 px-3">Tipo</th><th className="py-2 px-3 text-right">Total</th></tr></thead>
                  <tbody>
                     {trxs.map(t => (
                        <tr key={t.id} className="border-b border-slate-100">
                           <td className="py-2 px-3 text-slate-600">{t.fecha}</td>
                           <td className="py-2 px-3 font-bold text-slate-800">{t.tienda}</td>
                           <td className="py-2 px-3 text-slate-700">{t.pagadorEmail ? obtenerNombreCorto(t.pagadorEmail) : t.persona}</td>
                           <td className="py-2 px-3 font-bold">{t.tipo === 'Gasto' ? '🧾 Gasto' : '💸 Pago'}</td>
                           <td className="py-2 px-3 text-right font-black text-slate-900">S/ {t.monto.toFixed(2)}</td>
                        </tr>
                     ))}
                  </tbody>
               </table>
            </div>
            <div className="mt-12 text-center text-[10px] text-slate-400 font-bold uppercase tracking-widest">
               Fin del Reporte - CuentasClaras App
            </div>
        </div>

        {/* FIN ESTRUCTURA PDF */}


        <button onClick={() => cambiarVista('grupos')} className="text-[10px] font-black text-slate-400 hover:text-slate-800 mb-8 flex items-center gap-2 transition-colors uppercase tracking-widest bg-white py-2 px-4 rounded-full shadow-sm border border-slate-100 w-fit active:scale-95 print:hidden">← Volver a Grupos</button>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-6 print:hidden">
          <div className="flex items-center gap-5">
            <div className={`h-20 w-20 bg-white border border-slate-200 rounded-[24px] flex items-center justify-center text-4xl shadow-sm ${!grupo.activo ? 'grayscale' : ''}`}>{grupo.icono}</div>
            <div className="flex-1 min-w-0">
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight flex items-center gap-3 truncate">{grupo.nombre} {!grupo.activo && <span className="text-[10px] bg-slate-200 text-slate-500 px-3 py-1 rounded-full uppercase tracking-widest shadow-inner">Archivo</span>}</h2>
              <div className="flex -space-x-2 mt-2">
                {implicadosGrupo.slice(0,4).map((e, i) => (<div key={i} className="h-6 w-6 rounded-full bg-slate-800 border border-white flex justify-center items-center text-[8px] font-black text-white" title={obtenerNombreCorto(e)}>{obtenerNombreCorto(e).charAt(0).toUpperCase()}</div>))}
                {implicadosGrupo.length > 4 && <div className="h-6 w-6 rounded-full bg-slate-200 border border-white flex justify-center items-center text-[8px] font-black text-slate-600">+{implicadosGrupo.length - 4}</div>}
              </div>
            </div>
          </div>
          {grupo.activo ? (
            <div className="flex gap-3 w-full md:w-auto">
              <button onClick={() => abrirFormulario(grupo.id)} className="flex-1 md:flex-none bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-transform shadow-md shadow-emerald-600/20 active:scale-95">+ Gasto</button>
            </div>
          ) : <div className="bg-slate-100 text-slate-500 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest border border-slate-200">🔒 Grupo Liquidado</div>}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 print:hidden">
           <div className="lg:col-span-2 space-y-6 lg:space-y-8">
              <div className={`p-8 rounded-[32px] border shadow-sm flex flex-col justify-center ${!grupo.activo ? 'bg-slate-50 border-slate-200' : miSaldoG > 0.01 ? 'bg-emerald-50 border-emerald-100' : miSaldoG < -0.01 ? 'bg-rose-50 border-rose-100' : 'bg-slate-50 border-slate-200'}`}>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Tu Balance General</p>
                <h3 className={`text-6xl font-black tracking-tighter ${!grupo.activo ? 'text-slate-700' : miSaldoG > 0.01 ? 'text-emerald-600' : miSaldoG < -0.01 ? 'text-rose-600' : 'text-slate-700'}`}>S/ {Math.abs(miSaldoG).toFixed(2)}</h3>
                <p className="text-sm font-bold text-slate-600 mt-3 bg-white/50 px-4 py-2 rounded-lg w-fit shadow-sm border border-white/50">{!grupo.activo ? 'Liquidado y Cerrado' : miSaldoG > 0.01 ? `En total te deben` : miSaldoG < -0.01 ? `En total debes` : 'Cuentas claras'}</p>
              </div>

              <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm p-4 md:p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 ml-2 mr-2">
                   <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Historial</h4>
                   <div className="lg:hidden flex items-center bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 w-full focus-within:border-emerald-400 transition-colors"><span className="text-slate-400 text-xs">🔍</span><input type="text" value={busquedaGlobal} onChange={(e) => setBusquedaGlobal(e.target.value)} placeholder="Buscar concepto..." className="bg-transparent border-none outline-none ml-2 text-xs w-full text-slate-700 font-bold" /></div>
                </div>
                <div className="space-y-2 max-h-[50vh] md:max-h-[500px] overflow-y-auto custom-scrollbar pr-2">
                  {trxsFiltradas.length === 0 ? <div className="p-12 text-center text-slate-400 italic font-medium">El libro está vacío. Registra el primer gasto.</div> : trxsFiltradas.map(trx => <TransactionItem key={trx.id} trx={trx} grupo={grupo} miEmail={miEmailLogico} miNombre={miNombre} onSelect={setTransaccionSeleccionada} implicadosGrupo={implicadosGrupo} formateador={obtenerNombreCorto} /> )}
                </div>
              </div>
           </div>

           <div className="lg:col-span-1 space-y-6 lg:space-y-8">
              <div className="bg-white p-6 rounded-[32px] border border-slate-200 shadow-sm flex flex-col relative overflow-hidden">
                 <div className="absolute top-0 right-0 p-4 opacity-5 text-6xl transform translate-x-4 -translate-y-4 font-black">💸</div>
                 <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 relative z-10">Centro de Liquidación</p>
                 
                 {misPagosPendientes.length === 0 ? (
                    <div className="py-6 text-center">
                       <span className="text-4xl opacity-50 mb-2 block">✔️</span>
                       <p className="text-xs font-bold text-slate-500">No tienes deudas pendientes que saldar en este grupo.</p>
                    </div>
                 ) : (
                    <div className="space-y-3 relative z-10 max-h-60 overflow-y-auto custom-scrollbar pr-2">
                       {misPagosPendientes.map((pago, index) => (
                          <div key={index} className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex flex-col gap-3">
                             <div className="flex justify-between items-center">
                                <span className="text-xs font-bold text-slate-600">Debes pagar a <span className="font-black text-slate-900">{obtenerNombreCorto(pago.acreedorEmail)}</span></span>
                                <span className="text-sm font-black text-rose-500">S/ {pago.monto.toFixed(2)}</span>
                             </div>
                             <button onClick={() => setPagoPendienteConf(pago)} className="w-full bg-white border border-slate-200 text-slate-700 hover:border-emerald-400 hover:text-emerald-700 font-black py-2.5 rounded-xl text-[10px] uppercase tracking-widest transition-colors shadow-sm active:scale-95">
                                Liquidar esto
                             </button>
                          </div>
                       ))}
                    </div>
                 )}
              </div>

              <div className="bg-white p-6 rounded-[32px] border border-slate-200 shadow-sm flex flex-col">
                 <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Integrantes ({implicadosGrupo.length})</p>
                 <div className="space-y-3 max-h-48 overflow-y-auto custom-scrollbar">
                    {implicadosGrupo.map(email => {
                       const saldoInd = balancesGrupo[email.toLowerCase()] || 0;
                       
                       return (
                         <div key={email} className="flex items-center gap-3">
                           <div className="h-8 w-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-xs font-black text-slate-700">{obtenerNombreCorto(email).charAt(0).toUpperCase()}</div>
                           <div className="flex flex-col flex-1 min-w-0">
                             <p className="text-xs font-bold text-slate-800 truncate">{obtenerNombreCorto(email)}</p>
                             <p className={`text-[8px] uppercase tracking-widest truncate ${saldoInd > 0.01 ? 'text-emerald-500' : saldoInd < -0.01 ? 'text-rose-400' : 'text-slate-400'}`}>
                               {saldoInd > 0.01 ? `Le deben S/ ${saldoInd.toFixed(2)}` : saldoInd < -0.01 ? `Debe S/ ${Math.abs(saldoInd).toFixed(2)}` : 'Saldado'}
                             </p>
                           </div>
                         </div>
                       )
                    })}
                 </div>
              </div>
              
              {/* 🎯 SECCIÓN ADMIN & REPORTES PRO */}
              <div className="bg-slate-900 rounded-[32px] shadow-xl p-6 md:p-8 text-white relative overflow-hidden">
                <div className="absolute -right-6 -bottom-6 h-32 w-32 rounded-full bg-white/5 blur-2xl"></div>
                <h4 className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-6 text-center">Admin & Reportes</h4>
                
                <div className="flex flex-col gap-4 relative z-10">
                  <div className="bg-white/10 p-5 rounded-2xl border border-white/10 shadow-sm flex flex-col">
                     <h5 className="text-sm font-black text-white mb-1">Exportar Reportes</h5>
                     <p className="text-[10px] text-slate-400 mb-4 font-medium">Genera resúmenes ejecutivos listos para compartir.</p>
                     <div className="flex gap-2 mt-auto">
                        <button onClick={() => window.print()} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-transform active:scale-95 shadow-lg shadow-emerald-500/20">📄 PDF</button>
                        <button onClick={exportarExcel} className="flex-1 bg-white text-slate-900 hover:bg-slate-100 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-transform active:scale-95 shadow-lg">📊 Excel</button>
                     </div>
                  </div>

                  <div className="bg-white p-5 rounded-2xl shadow-sm flex flex-col">
                     <h5 className="text-sm font-black text-slate-800 mb-1">Liquidar Grupo</h5>
                     <p className="text-[10px] text-slate-500 mb-4 font-medium">Archiva el grupo. Requiere balance en S/ 0.00.</p>
                     <button onClick={intentarLiquidarGrupo} className={`mt-auto w-full py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-transform ${!grupo.activo ? 'bg-slate-100 text-slate-400 border border-slate-200' : 'bg-slate-900 text-white hover:bg-slate-800 shadow-md active:scale-95'}`}>{grupo.activo ? 'Archivar' : 'Cerrado'}</button>
                  </div>
                  
                  <div className="bg-white p-5 rounded-2xl border border-rose-100 shadow-sm flex flex-col">
                     <h5 className="text-sm font-black text-rose-600 mb-1">Destrucción</h5>
                     <p className="text-[10px] text-slate-500 mb-4 font-medium">Borra el grupo y su historial. Irreversible.</p>
                     <button onClick={() => setMostrarConfirmacionEliminar(true)} className="mt-auto w-full py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-transform bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 shadow-sm active:scale-95">Eliminar</button>
                  </div>
                </div>
              </div>

           </div>
        </div>
      </div>
    );
  };

  const renderAmigos = () => {
    const queryParaBuscar = busquedaGlobal || busquedaAmigos;
    const amigosFiltrados = listaDirectorioAmigos.filter(a => a.email.toLowerCase().includes(queryParaBuscar.toLowerCase()) || obtenerNombreCorto(a.email).toLowerCase().includes(queryParaBuscar.toLowerCase()));

    return (
      <div className="animate-in fade-in duration-300 pb-24 md:pb-10 max-w-5xl mx-auto">
        <div className="flex justify-between items-start md:items-center mb-6 md:mb-8 gap-3">
          <div className="flex-1 min-w-0"><h2 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight truncate">Directorio</h2><p className="text-xs md:text-sm text-slate-500 mt-1 font-medium truncate">Contactos validados en tu red.</p></div>
          {/* 🛡️ CORRECCIÓN MÓVIL: Botón siempre visible */}
          <button onClick={() => setMostrarFormularioAmigo(true)} className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 md:px-5 md:py-3 rounded-xl text-[10px] md:text-xs font-black uppercase transition-transform shadow-sm shrink-0 whitespace-nowrap flex items-center gap-2 active:scale-95">+ Agregar Amigo</button>
        </div>

        <div className="lg:hidden mb-6"><div className="flex items-center bg-white border border-slate-200 rounded-2xl px-4 py-3 shadow-sm focus-within:border-emerald-400 transition-colors w-full"><span className="text-slate-400">🔍</span><input type="text" value={busquedaAmigos} onChange={(e) => setBusquedaAmigos(e.target.value)} placeholder="Buscar amigo por nombre o correo..." className="bg-transparent border-none outline-none ml-3 text-sm w-full text-slate-700 font-bold placeholder-slate-400" />{busquedaAmigos && <button onClick={() => setBusquedaAmigos('')} className="text-slate-400 font-bold ml-2">×</button>}</div></div>

        {amigosFiltrados.length === 0 ? (
           <div className="bg-white border border-slate-200 rounded-[32px] p-16 text-center shadow-sm">
              <span className="text-6xl opacity-50">👥</span>
              <h3 className="text-2xl font-black text-slate-800 mt-6 tracking-tight">{queryParaBuscar ? 'Ningún contacto coincide' : 'No hay conexiones'}</h3>
              <p className="text-sm text-slate-500 mt-2 font-medium mb-6">{queryParaBuscar ? 'Intenta escribiendo el nombre o correo de otra forma.' : 'Agrega a alguien con su correo para que aparezca aquí.'}</p>
              {!queryParaBuscar && (<button onClick={() => setMostrarFormularioAmigo(true)} className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest mt-6 transition-transform shadow-sm active:scale-95">Agregar Amigo</button>)}
           </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {amigosFiltrados.map(amigo => (
              <div key={amigo.email} onClick={() => setAmigoSeleccionadoModal(amigo)} className="bg-white p-6 rounded-[24px] border border-slate-200 shadow-sm hover:border-emerald-400 hover:shadow-md transition-all group flex flex-col cursor-pointer relative overflow-hidden active:scale-[0.98]">
                <div className="absolute top-0 right-0 p-4 opacity-5 text-6xl transform translate-x-4 -translate-y-4 font-black">👥</div>
                <div className="flex items-center gap-4 mb-6 relative z-10">
                   <div className="h-14 w-14 bg-slate-900 rounded-full flex items-center justify-center text-xl text-white font-black group-hover:bg-emerald-600 transition-colors">{obtenerNombreCorto(amigo.email).charAt(0).toUpperCase()}</div>
                   <div className="overflow-hidden flex-1">
                      <h3 className="text-sm font-black text-slate-800 leading-tight truncate">{obtenerNombreCorto(amigo.email)}</h3>
                      <p className="text-[10px] text-slate-400 font-bold tracking-widest mt-1 truncate">{amigo.email}</p>
                   </div>
                </div>
                <div className="mt-auto pt-4 border-t border-slate-100 flex justify-between items-center relative z-10"><div><p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-1">Amigos desde</p><p className="text-sm font-black text-slate-800">{new Date(amigo.timestamp).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })}</p></div><span className="text-[10px] text-slate-400 uppercase font-black bg-slate-50 px-3 py-1 rounded-lg group-hover:bg-emerald-100 group-hover:text-emerald-700 transition-colors">Ver Perfil</span></div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderDetalleAmigo = () => {
    const amigo = listaDirectorioAmigos.find(a => a.email === amigoActivoEmail); 
    if (!amigo) return null;

    return (
      <div className="animate-in fade-in duration-300 max-w-4xl mx-auto pb-24 md:pb-10">
        <button onClick={() => cambiarVista('amigos')} className="text-[10px] font-black text-slate-400 hover:text-slate-800 mb-8 flex items-center gap-2 transition-colors uppercase tracking-widest bg-white py-2 px-4 rounded-full shadow-sm border border-slate-100 w-fit active:scale-95">
          ← Volver a Directorio
        </button>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
          <div className="flex items-center gap-5">
            <div className="h-20 w-20 bg-slate-900 border border-slate-800 rounded-[24px] flex items-center justify-center text-4xl font-black text-white shadow-lg">{obtenerNombreCorto(amigo.email).charAt(0).toUpperCase()}</div>
            <div>
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight break-all">{obtenerNombreCorto(amigo.email)}</h2>
              <p className="text-xs text-slate-500 font-bold tracking-widest mt-2">{amigo.email}</p>
            </div>
          </div>
          <div className="flex gap-3 w-full md:w-auto">
            <button onClick={() => mostrarAlerta("Próximamente", "La función para enviar recordatorios por WhatsApp estará lista pronto.", "📱", "emerald")} className="flex-1 md:flex-none bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100 px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-transform shadow-sm active:scale-95">
              ✉️ Enviar Recordatorio
            </button>
          </div>
        </div>

        <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm p-4 md:p-6">
          <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6 ml-2">Grupos que comparten ({amigo.cantidadGrupos})</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             {amigo.gruposCompartidos.map(g => (
               <div key={g.id} onClick={() => cambiarVista('detalleGrupo', g.id)} className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex items-center gap-4 hover:border-emerald-300 hover:bg-white hover:shadow-md transition-all cursor-pointer group active:scale-[0.98]">
                  <div className="h-12 w-12 bg-white rounded-xl flex items-center justify-center text-xl border border-slate-100 group-hover:bg-emerald-50 transition-colors shadow-sm">{g.icono}</div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-black text-slate-800 leading-tight truncate">{g.nombre}</h3>
                    <p className="text-[10px] text-emerald-600 uppercase font-bold mt-1 tracking-widest">Ir al grupo →</p>
                  </div>
               </div>
             ))}
          </div>
        </div>
      </div>
    );
  };

  const renderActividad = () => (
    <div className="animate-in fade-in duration-300 max-w-3xl mx-auto pb-24 md:pb-10">
      <div className="mb-8"><h2 className="text-3xl font-black text-slate-900 tracking-tight">Actividad Global</h2><p className="text-sm text-slate-500 mt-1 font-medium">Historial completo y cronológico de todos los movimientos.</p></div>
      <div className="lg:hidden mb-6"><div className="flex items-center bg-white border border-slate-200 rounded-2xl px-4 py-3 shadow-sm focus-within:border-emerald-400 transition-colors w-full"><span className="text-slate-400">🔍</span><input type="text" value={busquedaGlobal} onChange={(e) => setBusquedaGlobal(e.target.value)} placeholder="Buscar transacciones..." className="bg-transparent border-none outline-none ml-3 text-sm w-full text-slate-700 font-bold placeholder-slate-400" />{busquedaGlobal && <button onClick={() => setBusquedaGlobal('')} className="text-slate-400 font-bold ml-2">×</button>}</div></div>
      <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm p-4 overflow-hidden">
        {miHistorial.length === 0 ? <div className="p-16 text-center text-sm font-medium text-slate-400 italic">El libro está vacío.</div> : (
          <div className="space-y-1">
            {miHistorial.filter(trx => trx.tienda.toLowerCase().includes(busquedaGlobal.toLowerCase()) || trx.persona.toLowerCase().includes(busquedaGlobal.toLowerCase())).map(trx => {
               const grupo = grupos.find(g => g.id === trx.grupoId);
               return <TransactionItem key={trx.id} trx={trx} grupo={grupo} miEmail={miEmailLogico} miNombre={miNombre} onSelect={setTransaccionSeleccionada} implicadosGrupo={grupo ? [...(grupo.miembros || []), ...(grupo.invitados || [])] : []} formateador={obtenerNombreCorto} />
            })}
          </div>
        )}
      </div>
    </div>
  );

  const renderPerfil = () => (
    <div className="animate-in fade-in duration-300 max-w-2xl mx-auto pb-24 md:pb-10">
      <div className="mb-10"><h2 className="text-3xl font-black text-slate-900 tracking-tight">Mi Perfil</h2><p className="text-sm text-slate-500 mt-1 font-medium">Gestiona tu identidad pública.</p></div>
      <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm p-8 flex flex-col items-center mb-8 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-32 bg-emerald-500/10"></div>
        <div className="h-28 w-28 rounded-full bg-slate-900 border-4 border-white flex items-center justify-center text-4xl text-white font-black shadow-xl z-10 relative">{miNombre.charAt(0).toUpperCase()}<button className="absolute bottom-0 right-0 h-8 w-8 bg-emerald-500 hover:bg-emerald-600 rounded-full text-white text-xs flex items-center justify-center border-2 border-white transition-transform active:scale-95" title="Cambiar foto">📷</button></div>
        <h3 className="text-xl font-black text-slate-800 mt-4 z-10">{miNombre}</h3><p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1 z-10">{usuarioReal.email}</p>
      </div>
      <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm p-8">
        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Datos Personales</h4>
        <div className="space-y-5">
          <div><label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">Nombre para mostrar</label><input type="text" value={nombreEditado} onChange={(e) => setNombreEditado(e.target.value)} className="w-full p-4 bg-slate-50 rounded-2xl text-sm font-bold border border-slate-200 focus:border-emerald-500 outline-none mt-2 transition-colors" /></div>
          <div><label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">Correo Electrónico (Solo lectura)</label><input type="email" value={usuarioReal.email} disabled className="w-full p-4 bg-slate-100/50 rounded-2xl text-sm font-bold text-slate-400 border border-slate-200 outline-none mt-2 cursor-not-allowed" /></div>
          <div className="pt-6 border-t border-slate-100"><button onClick={guardarCambiosPerfil} className="bg-emerald-600 text-white px-8 py-4 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-emerald-700 transition-transform shadow-lg shadow-emerald-600/20 w-full md:w-auto active:scale-95">Guardar Cambios</button></div>
        </div>
      </div>
      <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm p-8 mt-6">
        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Seguridad de la Cuenta</h4>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4"><div><p className="text-sm font-bold text-slate-800">Contraseña de acceso</p><p className="text-xs text-slate-500 mt-1 font-medium">Mantén tu cuenta segura. Te enviaremos un enlace encriptado a tu correo.</p></div><button onClick={solicitarCambioPassword} className="bg-slate-900 text-white px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-800 transition-transform shadow-md w-full md:w-auto active:scale-95">Cambiar Contraseña</button></div>
      </div>
    </div>
  );

  const renderAjustes = () => (
    <div className="animate-in fade-in duration-300 max-w-2xl mx-auto pb-24 md:pb-10">
      <div className="mb-10"><h2 className="text-3xl font-black text-slate-900 tracking-tight">Ajustes</h2><p className="text-sm text-slate-500 mt-1 font-medium">Configura el comportamiento de tu cuenta.</p></div>
      <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm p-8 mb-6">
        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Notificaciones</h4>
        <div className="space-y-6">
          <div className="flex items-center justify-between"><div><p className="text-sm font-bold text-slate-800">Alertas en la app (Push)</p><p className="text-xs text-slate-500 mt-1 font-medium">Recibe avisos en la campana cuando te agreguen a un gasto.</p></div><ToggleSwitch activo={notifPush} onChange={() => setNotifPush(!notifPush)} /></div>
          <div className="flex items-center justify-between pt-6 border-t border-slate-50"><div><p className="text-sm font-bold text-slate-800">Correos electrónicos</p><p className="text-xs text-slate-500 mt-1 font-medium">Resumen semanal de deudas y saldos pendientes.</p></div><ToggleSwitch activo={notifEmail} onChange={() => setNotifEmail(!notifEmail)} /></div>
        </div>
      </div>
      <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm p-8 mb-6">
        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Preferencias Regionales</h4>
        <div className="space-y-5"><div className="flex flex-col md:flex-row md:items-center justify-between gap-2"><p className="text-sm font-bold text-slate-800">Moneda Principal</p><select className="p-3 bg-slate-50 rounded-xl text-sm font-bold border border-slate-200 outline-none w-full md:w-48 cursor-pointer"><option>PEN (Soles - S/)</option><option>USD (Dólares - $)</option><option>EUR (Euros - €)</option></select></div></div>
      </div>
    </div>
  );

  const renderPremium = () => (
    <div className="animate-in fade-in duration-500 py-16 text-center max-w-lg mx-auto pb-24 md:pb-10">
      <div className="inline-flex items-center justify-center h-28 w-28 rounded-[32px] bg-amber-100 text-amber-500 text-6xl mb-8 shadow-sm">👑</div>
      <h2 className="text-4xl font-black text-slate-900 mb-4 tracking-tight">CuentasClaras Pro</h2>
      <p className="text-slate-500 mb-10 text-lg font-medium leading-relaxed">El estándar profesional para gestionar deudas. Exporta reportes y automatiza tus finanzas.</p>
      <div className="bg-white p-8 rounded-[32px] border border-slate-200 shadow-sm text-left mb-10">
         <ul className="space-y-6 text-sm text-slate-700 font-semibold">
           <li className="flex items-center gap-4"><span className="text-xl bg-emerald-100 text-emerald-600 rounded-full h-10 w-10 flex items-center justify-center shrink-0">✓</span> Exportación a Excel y PDF</li>
           <li className="flex items-center gap-4"><span className="text-xl bg-emerald-100 text-emerald-600 rounded-full h-10 w-10 flex items-center justify-center shrink-0">✓</span> Grupos y miembros ilimitados</li>
           <li className="flex items-center gap-4"><span className="text-xl bg-emerald-100 text-emerald-600 rounded-full h-10 w-10 flex items-center justify-center shrink-0">✓</span> Múltiples divisas en tiempo real</li>
         </ul>
      </div>
      <button className="bg-slate-900 text-white font-black py-4 px-12 rounded-2xl hover:bg-slate-800 transition-transform shadow-xl shadow-slate-900/20 uppercase tracking-widest text-sm w-full md:w-auto active:scale-95" onClick={() => mostrarAlerta("Próximamente", "Esta función estará disponible muy pronto.", "🚀", "amber")}>Actualizar por S/ 15.00/mes</button>
    </div>
  );

  // ==========================================
  // 8. RENDER GENERAL
  // ==========================================
  return (
    <div className="flex h-[100dvh] w-full bg-[#F8FAFC] font-sans text-slate-900 overflow-hidden antialiased">
      
      {/* 3️⃣ LÍNEA DE PROGRESO DINÁMICA GLOBAL PARA TODAS LAS ACCIONES DENTRO DE LA APP */}
      <div className={`fixed top-0 left-0 w-full h-1.5 z-[9999] transition-opacity duration-300 ${progresoGlobal > 0 && !cargandoAuth ? 'opacity-100' : 'opacity-0'} print:hidden`}>
         <div className="h-full bg-emerald-500 transition-all duration-200 ease-out shadow-[0_0_12px_rgba(16,185,129,0.9)] relative" style={{ width: `${progresoGlobal}%` }}>
           <div className="absolute top-0 right-0 h-full w-20 bg-gradient-to-r from-transparent to-white opacity-50"></div>
         </div>
      </div>

      <aside className="hidden md:flex flex-col w-[280px] bg-[#0F172A] text-slate-300 shrink-0 shadow-2xl z-30 print:hidden">
        <div className="h-24 flex items-center px-8 border-b border-slate-800 shrink-0"><div className="h-10 w-10 rounded-[14px] bg-emerald-500 flex items-center justify-center text-white font-black shadow-lg shadow-emerald-500/20 text-xl mr-3">C</div><span className="text-2xl font-black text-white tracking-tight italic">CuentasClaras</span></div>
        <nav className="flex-1 py-8 px-5 space-y-2 overflow-y-auto custom-scrollbar">
          <p className="px-3 text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4">Navegación</p>
          <SidebarItem icono="📊" texto="Dashboard" vista="dashboard" /><SidebarItem icono="📁" texto="Mis Grupos" vista="grupos" /><SidebarItem icono="👥" texto="Amigos" vista="amigos" /><SidebarItem icono="📋" texto="Actividad" vista="actividad" />
          <p className="px-3 text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4 mt-8">Negocio</p>
          <SidebarItem icono="👑" texto="Premium" vista="premium" extraClass="text-amber-400/90 hover:text-amber-300" />
        </nav>
      </aside>

      <main className="flex-1 flex flex-col min-w-0 h-full relative print:!static print:!h-auto print:!overflow-visible" onClick={() => { setMostrarNotificaciones(false); setMostrarMenuPerfil(false); setDropdownGrupoAbierto(false); setMostrarResultados(false); }}>
        
        <header className="h-20 md:h-24 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-10 shrink-0 z-30 print:hidden">
          <div className="flex items-center gap-4 flex-1">
             <h1 className="hidden md:block text-xl font-black capitalize tracking-tight w-48">{vistaActiva === 'detalleGrupo' ? 'Gestión' : vistaActiva}</h1>
             <div className="relative hidden lg:block w-96">
               <div className={`flex items-center bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 transition-all ${mostrarResultados ? 'border-emerald-400 bg-white shadow-sm' : ''}`}>
                  <span className="text-slate-400">🔍</span><input type="text" value={busquedaGlobal} onChange={(e) => {setBusquedaGlobal(e.target.value); setMostrarResultados(true);}} onClick={(e) => {e.stopPropagation(); setMostrarResultados(true);}} placeholder="Buscar grupos o amigos..." className="bg-transparent border-none outline-none ml-2 text-sm w-full font-medium placeholder-slate-400 text-slate-700" />
                  {busquedaGlobal && <button onClick={(e) => {e.stopPropagation(); setBusquedaGlobal(''); setMostrarResultados(false);}} className="text-slate-400 font-bold hover:text-slate-600 ml-2 text-lg leading-none">&times;</button>}
               </div>
               
               {/* RESULTADOS BÚSQUEDA */}
               {mostrarResultados && busquedaGlobal && (
                 <div className="absolute top-full mt-2 left-0 w-full bg-white rounded-[24px] shadow-2xl border border-slate-100 z-50 overflow-hidden py-2" onClick={(e)=>e.stopPropagation()}>
                    {resultadosBusqueda.grupos.length === 0 && resultadosBusqueda.amigos.length === 0 ? (
                       <div className="p-6 text-center text-xs font-bold text-slate-400 uppercase tracking-widest">No hay coincidencias</div>
                    ) : (
                      <div className="max-h-64 overflow-y-auto custom-scrollbar">
                        {resultadosBusqueda.grupos.length > 0 && (
                          <div className="mb-2"><p className="px-4 py-2 text-[10px] font-black text-emerald-600 uppercase tracking-widest bg-emerald-50/50">📁 En Grupos</p>
                             {resultadosBusqueda.grupos.map(g => <div key={g.id} onClick={() => cambiarVista('detalleGrupo', g.id)} className="px-4 py-3 hover:bg-slate-50 flex items-center gap-3 cursor-pointer transition-colors"><span className="text-xl">{g.icono}</span><span className="text-sm font-bold text-slate-700">{g.nombre}</span></div>)}
                          </div>
                        )}
                        {resultadosBusqueda.amigos.length > 0 && (
                          <div><p className="px-4 py-2 text-[10px] font-black text-emerald-600 uppercase tracking-widest bg-emerald-50/50">👥 En Contactos</p>
                             {resultadosBusqueda.amigos.map(a => <div key={a.email} onClick={() => { cambiarVista('amigos'); setAmigoSeleccionadoModal(a); }} className="px-4 py-3 hover:bg-slate-50 flex items-center gap-3 cursor-pointer transition-colors"><div className="h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-black">{obtenerNombreCorto(a.email).charAt(0).toUpperCase()}</div><span className="text-sm font-bold text-slate-700">{obtenerNombreCorto(a.email)}</span></div>)}
                          </div>
                        )}
                      </div>
                    )}
                 </div>
               )}
             </div>
             <h1 className="md:hidden text-lg font-black tracking-tight ml-2">CuentasClaras</h1>
          </div>
          
          <div className="flex items-center gap-1 md:gap-3">
             <div className="relative">
               {/* 🔔 BOTÓN DE CAMPANA ENLAZADO A LA NUEVA FUNCIÓN */}
               <button onClick={togglePanelNotificaciones} className="relative p-2.5 text-2xl text-slate-400 hover:text-emerald-600 transition-colors rounded-full hover:bg-slate-50">
                 🔔{nuevasNotificacionesCount > 0 && <span className="absolute top-2.5 right-2.5 h-2.5 w-2.5 bg-rose-500 border-2 border-white rounded-full animate-pulse"></span>}
               </button>
               {mostrarNotificaciones && (
                 <div className="absolute top-14 right-0 w-80 md:w-96 bg-white rounded-[24px] shadow-2xl border border-slate-100 p-5 z-50 animate-in fade-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-between items-center mb-4 pb-3 border-b border-slate-100"><h4 className="text-sm font-black text-slate-800">Notificaciones</h4>{nuevasNotificacionesCount > 0 && <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest">{nuevasNotificacionesCount} Nuevas</span>}</div>
                    <div className="max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar">
                      
                      {solicitudesAmistad.length > 0 && (
                         <div className="mb-5"><p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-3 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-500"></span> Solicitudes de Amigos</p>
                           {solicitudesAmistad.map(sol => (
                              <div key={sol.id} className="bg-emerald-50/50 p-4 rounded-[16px] border border-emerald-100 mb-2">
                                 <p className="text-xs font-bold text-slate-600"><span className="text-slate-900 font-black">{sol.deNombre}</span> quiere agregarte.</p><p className="text-sm font-black text-emerald-800 mt-1 mb-3">{sol.de}</p>
                                 <div className="flex gap-2"><button onClick={() => procesarSolicitudAmistad(sol, 'aceptar')} className="flex-1 bg-emerald-600 text-white text-[10px] font-black py-2 rounded-xl hover:bg-emerald-700 uppercase tracking-widest transition-transform active:scale-95">Aceptar</button><button onClick={() => procesarSolicitudAmistad(sol, 'rechazar')} className="flex-1 bg-white text-slate-500 text-[10px] font-black py-2 rounded-xl border border-slate-200 hover:bg-slate-50 uppercase tracking-widest transition-transform active:scale-95">Rechazar</button></div>
                              </div>
                           ))}
                         </div>
                      )}

                      {invitacionesPendientes.length > 0 && (
                         <div className="mb-5"><p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-3 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-500"></span> Invitaciones a Grupo</p>
                           {invitacionesPendientes.map(inv => (
                              <div key={inv.id} className="bg-emerald-50/50 p-4 rounded-[16px] border border-emerald-100 mb-2">
                                 <p className="text-xs font-bold text-slate-600"><span className="text-slate-900 font-black">{obtenerNombreCorto(inv.creadorEmail) || inv.creador}</span> te invitó a:</p><p className="text-sm font-black text-emerald-800 mt-1 mb-3">{inv.icono} {inv.nombre}</p>
                                 <div className="flex gap-2"><button onClick={() => aceptarInvitacion(inv)} className="flex-1 bg-emerald-600 text-white text-[10px] font-black py-2 rounded-xl hover:bg-emerald-700 uppercase tracking-widest transition-transform active:scale-95">Entrar</button><button onClick={() => rechazarInvitacion(inv)} className="flex-1 bg-white text-slate-500 text-[10px] font-black py-2 rounded-xl border border-slate-200 hover:bg-slate-50 uppercase tracking-widest transition-transform active:scale-95">Ignorar</button></div>
                              </div>
                           ))}
                         </div>
                      )}

                      {alertasGenerales.length > 0 && (
                         <div className="mb-5"><p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-3 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-indigo-400"></span> Avisos</p>
                           {alertasGenerales.map(alerta => (
                              <div key={alerta.id} className={`p-3 rounded-[16px] border mb-2 flex items-start justify-between ${alerta.tipo === 'error' ? 'bg-rose-50 border-rose-100' : alerta.tipo === 'info' ? 'bg-slate-50 border-slate-200' : 'bg-indigo-50 border-indigo-100'}`}>
                                 <p className={`text-xs font-bold leading-tight ${alerta.tipo === 'error' ? 'text-rose-700' : alerta.tipo === 'info' ? 'text-slate-600' : 'text-slate-700'}`}>{alerta.texto}</p>
                                 <button onClick={() => eliminarAlerta(alerta.id)} className="text-slate-400 hover:text-slate-600 font-bold ml-2 text-lg leading-none">&times;</button>
                              </div>
                           ))}
                         </div>
                      )}

                      {gastosOtrosRecientes.length > 0 && (
                         <div><p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-slate-300"></span> Actividad reciente</p>
                           {gastosOtrosRecientes.map(trx => (
                              <div key={trx.id} onClick={() => { setTransaccionSeleccionada(trx); setMostrarNotificaciones(false); }} className={`p-3 rounded-[16px] border mb-2 cursor-pointer hover:shadow-sm transition-all flex items-center gap-3 ${esNuevaNotif(trx.timestamp) ? 'bg-emerald-50/50 border-emerald-200' : 'bg-white border-slate-100 hover:border-slate-200'}`}>
                                 <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center text-lg shrink-0 border border-slate-100 shadow-sm">{trx.tipo === 'Saldado' ? '💸' : '🧾'}</div>
                                 <div><p className="text-xs text-slate-800 leading-tight"><span className="font-black">{trx.pagadorEmail ? obtenerNombreCorto(trx.pagadorEmail) : trx.persona}</span> registró <span className="font-bold text-slate-500">"{trx.tienda}"</span></p><p className="text-[10px] font-black text-emerald-600 mt-1 uppercase tracking-widest">S/ {trx.monto.toFixed(2)}</p></div>
                              </div>
                           ))}
                         </div>
                      )}
                      
                      {totalNotificaciones === 0 && <div className="py-8 text-center flex flex-col items-center justify-center"><span className="text-4xl opacity-40 mb-3">🔔</span><p className="text-sm text-slate-400 font-bold">Todo está al día.</p></div>}
                    </div>
                 </div>
               )}
             </div>
             <div className="relative">
               <button onClick={(e) => { e.stopPropagation(); setMostrarMenuPerfil(!mostrarMenuPerfil); setMostrarNotificaciones(false); setDropdownGrupoAbierto(false); }} className="flex items-center gap-2 hover:bg-slate-50 p-1.5 md:pl-2 md:pr-3 rounded-2xl transition-colors border border-transparent hover:border-slate-200">
                 <div className="h-9 w-9 rounded-full bg-slate-900 flex items-center justify-center text-white font-black text-sm shadow-sm">{miNombre.charAt(0)}</div><span className="hidden md:block text-[10px] font-black text-slate-400 uppercase tracking-widest mt-0.5">▼</span>
               </button>
               {mostrarMenuPerfil && (
                 <div className="absolute top-14 right-0 w-60 bg-white rounded-[24px] shadow-2xl border border-slate-100 p-2 z-50 animate-in fade-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
                    <div className="px-4 py-3 border-b border-slate-100 mb-2"><p className="text-sm font-black text-slate-900 truncate">{miNombre}</p><p className="text-[10px] font-bold text-slate-400 truncate mt-0.5 uppercase tracking-widest">{usuarioReal.email}</p></div>
                    <button onClick={() => cambiarVista('perfil')} className="w-full text-left px-4 py-3 text-xs font-black text-slate-600 hover:bg-slate-50 hover:text-emerald-600 rounded-xl transition-colors flex items-center gap-3 uppercase tracking-widest"><span className="text-lg">👤</span> Mi Perfil</button>
                    <button onClick={() => cambiarVista('ajustes')} className="w-full text-left px-4 py-3 text-xs font-black text-slate-600 hover:bg-slate-50 hover:text-emerald-600 rounded-xl transition-colors flex items-center gap-3 uppercase tracking-widest"><span className="text-lg">⚙️</span> Ajustes</button>
                    <button onClick={() => cambiarVista('premium')} className="w-full text-left px-4 py-3 text-xs font-black text-slate-600 hover:bg-slate-50 hover:text-amber-500 rounded-xl transition-colors flex items-center gap-3 uppercase tracking-widest md:hidden"><span className="text-lg">👑</span> Plan Pro</button>
                    <div className="border-t border-slate-100 mt-2 pt-2"><button onClick={cerrarSesion} className="w-full text-left px-4 py-3 text-xs font-black text-rose-500 hover:bg-rose-50 rounded-xl transition-colors flex items-center gap-3 uppercase tracking-widest"><span className="text-lg">🚪</span> Cerrar Sesión</button></div>
                 </div>
               )}
             </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-4 md:p-10 custom-scrollbar print:!p-0 print:!overflow-visible">
           <div className="max-w-6xl mx-auto pb-24 md:pb-0 print:!pb-0">
             {vistaActiva === 'dashboard' && renderDashboard()}
             {vistaActiva === 'grupos' && renderGrupos()}
             {vistaActiva === 'amigos' && renderAmigos()}
             {vistaActiva === 'actividad' && renderActividad()}
             {vistaActiva === 'detalleGrupo' && renderDetalleGrupo()}
             {vistaActiva === 'detalleAmigo' && renderDetalleAmigo()}
             {vistaActiva === 'perfil' && renderPerfil()}
             {vistaActiva === 'ajustes' && renderAjustes()}
             {vistaActiva === 'premium' && renderPremium()}
           </div>
        </div>
        
        {/* NAV INFERIOR MÓVIL ESTILO PRO */}
        <nav className="md:hidden fixed bottom-0 left-0 w-full bg-white border-t border-slate-200 flex justify-between items-center h-16 z-50 pb-safe shadow-[0_-10px_40px_rgba(0,0,0,0.05)] px-2 print:hidden">
          <button onClick={() => cambiarVista('dashboard')} className={`flex flex-col items-center justify-center w-14 h-full transition-colors ${vistaActiva === 'dashboard' ? 'text-emerald-600' : 'text-slate-400'}`}><span className="text-xl mb-0.5">📊</span><span className="text-[8px] font-black uppercase">Resumen</span></button>
          <button onClick={() => cambiarVista('grupos')} className={`flex flex-col items-center justify-center w-14 h-full transition-colors ${vistaActiva === 'grupos' || vistaActiva === 'detalleGrupo' ? 'text-emerald-600' : 'text-slate-400'}`}><span className="text-xl mb-0.5">📁</span><span className="text-[8px] font-black uppercase">Grupos</span></button>
          
          <div className="relative w-14 h-full flex justify-center">
            <button onClick={() => abrirFormulario()} className="absolute -top-6 h-14 w-14 bg-slate-900 text-white rounded-full shadow-xl shadow-slate-900/30 flex items-center justify-center text-3xl font-light active:scale-95 transition-transform border-[4px] border-[#F8FAFC] z-50">
              +
            </button>
          </div>

          <button onClick={() => cambiarVista('amigos')} className={`flex flex-col items-center justify-center w-14 h-full transition-colors ${vistaActiva === 'amigos' || vistaActiva === 'detalleAmigo' ? 'text-emerald-600' : 'text-slate-400'}`}><span className="text-xl mb-0.5">👥</span><span className="text-[8px] font-black uppercase">Amigos</span></button>
          <button onClick={() => cambiarVista('actividad')} className={`flex flex-col items-center justify-center w-14 h-full transition-colors ${vistaActiva === 'actividad' ? 'text-emerald-600' : 'text-slate-400'}`}><span className="text-xl mb-0.5">📋</span><span className="text-[8px] font-black uppercase">Actividad</span></button>
        </nav>

        {(!['premium', 'ajustes', 'perfil'].includes(vistaActiva)) && (
          <button onClick={() => abrirFormulario()} className="hidden md:flex fixed bottom-12 right-12 h-16 w-16 bg-slate-900 text-white rounded-[20px] shadow-2xl items-center justify-center text-3xl font-light hover:bg-slate-800 hover:-translate-y-1 hover:scale-105 active:scale-95 transition-all z-40 border border-slate-700 group print:hidden"><span className="group-hover:rotate-90 transition-transform duration-300 ease-out">+</span></button>
        )}
      </main>

      {/* --- MODALES INTERACTIVOS Y DE ADVERTENCIA --- */}
      <ModalAlerta config={alertaApp} onClose={() => setAlertaApp(null)} />

      {/* VISOR LIGHTBOX PARA RECIBOS */}
      {reciboAbierto && (
        <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-sm flex flex-col items-center justify-center z-[500] p-4 print:hidden" onClick={() => setReciboAbierto(null)}>
          <div className="relative w-full max-w-4xl flex flex-col items-center animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <button onClick={() => setReciboAbierto(null)} className="absolute -top-12 right-0 md:-right-12 md:top-0 bg-white/10 hover:bg-white/20 text-white rounded-full p-2 transition-colors">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>
            <img src={reciboAbierto} alt="Recibo adjunto" className="max-w-full max-h-[75vh] object-contain rounded-xl shadow-2xl mb-6 border border-white/10 bg-black/50" />
            <button onClick={() => descargarRecibo(reciboAbierto)} className="bg-emerald-500 hover:bg-emerald-600 text-white font-black py-4 px-8 rounded-2xl uppercase tracking-widest text-xs transition-transform active:scale-95 shadow-lg shadow-emerald-500/20 flex items-center gap-3">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg> Descargar Imagen
            </button>
          </div>
        </div>
      )}

      {/* MODAL DETALLE DE AMIGO */}
      {amigoSeleccionadoModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[120] p-4 print:hidden" onClick={() => {if(!mostrarConfirmacionEliminarAmigo) setAmigoSeleccionadoModal(null)}}>
          {mostrarConfirmacionEliminarAmigo ? (
             <div className="bg-white w-full max-w-sm rounded-[32px] p-8 shadow-2xl border-4 border-rose-100 text-center animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
               <div className="h-16 w-16 rounded-full bg-rose-100 flex items-center justify-center text-rose-500 text-3xl mx-auto mb-4">⚠️</div>
               <h3 className="text-xl font-black text-slate-900 mb-2 tracking-tight">¿Eliminar Contacto?</h3>
               <p className="text-sm text-slate-500 mb-8 font-medium leading-relaxed">Se borrará de tu libreta. <span className="font-black text-rose-500 block mt-2">Sus grupos en común y deudas no se alterarán.</span></p>
               <button onClick={ejecutarEliminacionAmigo} className="w-full py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-transform bg-rose-600 text-white shadow-lg shadow-rose-600/30 hover:bg-rose-700 mb-2 active:scale-95">Sí, Eliminar Contacto</button>
               <button onClick={() => setMostrarConfirmacionEliminarAmigo(false)} className="w-full text-slate-500 font-bold py-3 text-xs uppercase tracking-widest hover:bg-slate-50 rounded-2xl transition-colors active:scale-95">Volver</button>
             </div>
          ) : (
             <div className="bg-white w-full max-w-md rounded-[32px] p-6 md:p-8 shadow-2xl animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
               <div className="flex justify-between items-start mb-6">
                 <div className="flex items-center gap-4">
                    <div className="h-16 w-16 bg-slate-900 rounded-[20px] flex items-center justify-center text-2xl font-black text-white shadow-lg">{obtenerNombreCorto(amigoSeleccionadoModal.email).charAt(0).toUpperCase()}</div>
                    <div><h3 className="text-lg font-black text-slate-900 leading-tight truncate w-48 md:w-56">{obtenerNombreCorto(amigoSeleccionadoModal.email)}</h3><p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Amigos desde {new Date(amigoSeleccionadoModal.timestamp).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })}</p></div>
                 </div>
                 <button onClick={() => setAmigoSeleccionadoModal(null)} className="h-8 w-8 bg-slate-100 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors font-bold">&times;</button>
               </div>
               <div className="bg-slate-50 rounded-2xl border border-slate-100 p-4 mb-6">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Grupos Compartidos</h4>
                  {amigosMap[amigoSeleccionadoModal.email]?.gruposCompartidos.length === 0 ? (
                    <p className="text-xs text-slate-500 font-medium">Aún no comparten grupos.</p>
                  ) : (
                    <div className="flex flex-col gap-2 max-h-32 overflow-y-auto custom-scrollbar pr-2">
                       {amigosMap[amigoSeleccionadoModal.email]?.gruposCompartidos.map(g => (
                         <div key={g.id} onClick={() => { setAmigoSeleccionadoModal(null); cambiarVista('detalleGrupo', g.id); }} className="flex items-center gap-3 p-2 hover:bg-white rounded-xl transition-colors cursor-pointer border border-transparent hover:border-slate-200 hover:shadow-sm">
                           <div className="h-8 w-8 bg-white rounded-lg flex items-center justify-center border border-slate-100 shadow-sm text-sm">{g.icono}</div><span className="text-sm font-bold text-slate-700 flex-1 truncate">{g.nombre}</span><span className="text-slate-300">→</span>
                         </div>
                       ))}
                    </div>
                  )}
               </div>
               <div className="border-t border-slate-100 pt-4">
                 <button onClick={() => setMostrarConfirmacionEliminarAmigo(true)} className="w-full text-left px-4 py-3 text-xs font-black text-rose-500 hover:bg-rose-50 rounded-xl transition-colors flex items-center justify-center gap-2 uppercase tracking-widest border border-rose-100"><span className="text-lg">⚠️</span> Eliminar del directorio</button>
               </div>
             </div>
          )}
        </div>
      )}

      {/* MODAL PARA AGREGAR AMIGO */}
      {mostrarFormularioAmigo && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 print:hidden">
          <div className="bg-white w-full max-w-md rounded-[32px] p-8 shadow-2xl border border-white animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6"><h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">Agregar Amigo</h3><button onClick={() => setMostrarFormularioAmigo(false)} className="h-8 w-8 bg-slate-100 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors font-bold">&times;</button></div>
            {usuarioNoEncontrado ? (
              <div className="text-center space-y-4 py-2">
                 <div className="text-5xl mb-2">🤷‍♂️</div><h3 className="text-lg font-black text-slate-900 tracking-tight">Usuario no registrado</h3>
                 <p className="text-sm text-slate-500 font-medium leading-relaxed">El correo <span className="font-bold text-slate-800">{usuarioNoEncontrado}</span> no pertenece a ninguna cuenta de CuentasClaras.</p>
                 <div className="pt-4 flex flex-col gap-2">
                   <button onClick={() => { compartirApp(); setUsuarioNoEncontrado(null); setMostrarFormularioAmigo(false); }} className="w-full bg-emerald-600 text-white font-black py-4 rounded-2xl text-xs uppercase tracking-widest hover:bg-emerald-700 transition-transform shadow-xl shadow-emerald-600/20 active:scale-95">Enviar Invitación por Link</button>
                   <button onClick={() => setUsuarioNoEncontrado(null)} className="w-full text-slate-500 font-bold py-3 text-xs uppercase tracking-widest hover:bg-slate-50 rounded-2xl transition-transform active:scale-95">Intentar otro correo</button>
                 </div>
              </div>
            ) : (
              <div className="space-y-5">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Correo de tu amigo</label>
                  <input value={nuevoAmigoEmail} onChange={(e) => setNuevoAmigoEmail(e.target.value)} type="email" placeholder="correo@ejemplo.com" className="w-full p-4 bg-slate-50 rounded-2xl text-sm font-bold border border-slate-200 focus:border-emerald-500 outline-none mt-1 transition-colors" />
                  <p className="text-[10px] text-slate-400 mt-2 font-medium">Se enviará una solicitud. Tu amigo debe aceptarla para aparecer en tu libreta.</p>
                </div>
                <div className="pt-4 border-t border-slate-100 mt-2 flex flex-col gap-2">
                  <button onClick={enviarSolicitudAmistad} className="w-full bg-slate-900 text-white font-black py-4 rounded-2xl text-xs uppercase tracking-widest hover:bg-slate-800 transition-transform shadow-xl shadow-slate-900/20 active:scale-95">Enviar Solicitud</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODAL GASTO MULTIUSUARIO (CON SUBIDA DE RECIBOS) */}
      {mostrarFormularioGasto && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-end md:items-center justify-center z-[100] md:p-4 print:hidden">
          <div className="bg-white w-full md:max-w-md rounded-t-[40px] md:rounded-[32px] p-6 md:p-8 shadow-2xl animate-in slide-in-from-bottom md:zoom-in-95 duration-200" onClick={(e) => setDropdownGrupoAbierto(false)}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">{modoEdicion ? '✏️ Editar Gasto' : 'Transacción'}</h3>
              <button onClick={() => {setMostrarFormularioGasto(false); setModoEdicion(false); setIdGastoEdicion(null); setImagenAdjuntaBase64(null); setNombreArchivoVisible('');}} className="h-8 w-8 bg-slate-100 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors font-bold">&times;</button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Grupo destino</label>
                <div className="relative mt-1">
                  <div onClick={(e) => { e.stopPropagation(); if(!modoEdicion) setDropdownGrupoAbierto(!dropdownGrupoAbierto); }} className={`w-full p-3 rounded-2xl text-sm font-bold border flex justify-between items-center transition-colors shadow-sm ${modoEdicion ? 'bg-slate-100 border-slate-200 cursor-not-allowed opacity-70' : 'bg-slate-50 border-slate-200 cursor-pointer hover:border-emerald-400'}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{gruposActivos.find(g => g.id === inputGrupoId)?.icono || '📁'}</span>
                      <span className="text-slate-800 truncate">{gruposActivos.find(g => g.id === inputGrupoId)?.nombre || 'Selecciona un grupo'}</span>
                    </div>
                    {!modoEdicion && <span className={`text-slate-400 transition-transform duration-200 ${dropdownGrupoAbierto ? 'rotate-180' : ''}`}>▼</span>}
                  </div>

                  {dropdownGrupoAbierto && !modoEdicion && (
                    <div className="absolute top-full left-0 mt-2 w-full bg-white border border-slate-100 shadow-2xl shadow-slate-200/50 rounded-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="max-h-48 overflow-y-auto custom-scrollbar">
                        {gruposActivos.map(g => (
                          <div key={g.id} onClick={(e) => { e.stopPropagation(); setInputGrupoId(g.id); setDropdownGrupoAbierto(false); if (inputTipo === 'Gasto') setInputInvolucrados([...(g.miembros || []), ...(g.invitados || [])]); else setInputInvolucrados([]); }} className={`p-4 text-sm font-bold flex items-center gap-3 cursor-pointer transition-colors ${inputGrupoId === g.id ? 'bg-emerald-50/50 text-emerald-700' : 'hover:bg-slate-50 text-slate-700'}`}>
                            <span className="text-2xl">{g.icono}</span><span>{g.nombre}</span>{inputGrupoId === g.id && <span className="ml-auto text-emerald-500 font-black text-lg">✓</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-4">
                 <div className="flex-1">
                   <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">¿Quién Pagó?</label>
                   <select value={inputPagadorEmail} onChange={(e) => setInputPagadorEmail(e.target.value)} disabled={modoEdicion} className={`w-full p-3 rounded-2xl text-xs font-bold border outline-none mt-1 truncate ${modoEdicion ? 'bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed' : 'bg-slate-50 border-slate-200 cursor-pointer'}`}>
                      {gruposActivos.find(g => g.id === inputGrupoId) && [...(gruposActivos.find(g => g.id === inputGrupoId).miembros || []), ...(gruposActivos.find(g => g.id === inputGrupoId).invitados || [])].map(m => (
                         <option key={m} value={m}>{obtenerNombreCorto(m)}</option>
                      ))}
                   </select>
                 </div>
                 <div className="flex-1">
                   <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Monto Total</label>
                   <div className="relative mt-1">
                     <span className="absolute left-3 top-3 text-slate-400 font-black">S/</span>
                     <input type="number" value={inputMonto} onChange={(e)=>setInputMonto(e.target.value)} placeholder="0.00" className="w-full p-3 pl-8 bg-slate-50 rounded-2xl text-sm font-black border border-slate-200 focus:border-emerald-500 outline-none transition-colors shadow-inner" />
                   </div>
                 </div>
              </div>
              
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Concepto</label>
                <input type="text" value={inputDesc} onChange={(e)=>setInputDesc(e.target.value)} placeholder="Ej: Cena, Airbnb..." className="w-full p-3 bg-slate-50 rounded-2xl text-sm font-bold border border-slate-200 focus:border-emerald-500 outline-none mt-1 transition-colors" />
              </div>

              {/* 📸 SECCIÓN PRO: SUBIDA DE RECIBO (COMPRESIÓN LOCAL) */}
              {!modoEdicion && inputTipo === 'Gasto' && (
                <div className="pt-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Recibo / Factura (Opcional)</label>
                  <label className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-2xl border border-slate-200 cursor-pointer hover:border-emerald-400 transition-colors">
                     <div className="flex items-center gap-3 overflow-hidden">
                       <span className="text-xl">📷</span>
                       <span className="text-xs font-bold text-slate-600 truncate">{nombreArchivoVisible || 'Adjuntar foto...'}</span>
                     </div>
                     {imagenAdjuntaBase64 && <span className="text-emerald-500 font-black">✓</span>}
                     <input type="file" accept="image/jpeg, image/png" onChange={procesarAdjunto} className="hidden" />
                  </label>
                  <p className="text-[9px] text-slate-400 mt-1 font-bold px-1">La imagen se optimizará automáticamente para no consumir tus datos.</p>
                </div>
              )}
              
              <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100">
                <label className={`text-[10px] font-black uppercase tracking-widest mb-2 block ${inputTipo === 'Saldado' ? 'text-emerald-600' : 'text-slate-500'}`}>
                  {inputTipo === 'Saldado' ? '¿A quién le estás pagando? (Elige 1)' : 'Dividir en partes iguales entre:'}
                </label>
                <div className={`flex ${inputTipo === 'Saldado' ? 'flex-col' : 'flex-wrap'} gap-2 max-h-40 overflow-y-auto custom-scrollbar pr-1`}>
                  {gruposActivos.find(g => g.id === inputGrupoId) && [...(gruposActivos.find(g => g.id === inputGrupoId).miembros || []), ...(gruposActivos.find(g => g.id === inputGrupoId).invitados || [])].filter(m => inputTipo === 'Gasto' || m !== inputPagadorEmail).map(m => {
                     const seleccionado = inputInvolucrados.includes(m.toLowerCase());
                     let infoExtra = '';
                     let deshabilitado = false; 
                     
                     if (inputTipo === 'Saldado' && !modoEdicion) {
                         const pagadorActual = inputPagadorEmail.toLowerCase();
                         const deudaExacta = calcularDeudaExacta(pagadorActual, m.toLowerCase(), inputGrupoId);
                         
                         if (deudaExacta > 0.01) {
                             infoExtra = `Deuda S/ ${deudaExacta.toFixed(2)}`;
                         } else if (deudaExacta < -0.01) {
                             infoExtra = `A favor S/ ${Math.abs(deudaExacta).toFixed(2)}`;
                             deshabilitado = true; // Bloqueo: No le puedes pagar a quien te debe
                         } else {
                             infoExtra = 'Saldado';
                             deshabilitado = true; // Bloqueo: Ya le pagaste
                         }
                     }

                     return (
                       <button 
                         key={m} 
                         onClick={() => toggleInvolucrado(m)} 
                         disabled={deshabilitado}
                         className={`px-3 py-2 rounded-xl text-xs font-black border transition-all flex items-center justify-between 
                         ${deshabilitado ? 'bg-slate-100 border-slate-200 text-slate-400 opacity-50 cursor-not-allowed' : 
                           seleccionado ? (inputTipo === 'Saldado' ? 'bg-emerald-500 border-emerald-600 text-white shadow-md' : 'bg-emerald-100 border-emerald-200 text-emerald-700 active:scale-95') : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:shadow-sm active:scale-95'}`}
                       >
                         <span>{seleccionado ? '✓ ' : ''}{obtenerNombreCorto(m)}</span>
                         {inputTipo === 'Saldado' && !modoEdicion && <span className={`text-[9px] uppercase tracking-widest font-bold ${seleccionado ? 'text-emerald-100' : 'text-slate-400'}`}>({infoExtra})</span>}
                       </button>
                     )
                  })}
                </div>
                {inputTipo === 'Gasto' && inputMonto && inputInvolucrados.length > 0 && (<p className="text-[10px] text-slate-500 font-bold mt-3 text-center bg-slate-100 py-1.5 rounded-lg">S/ {(parseFloat(inputMonto) / inputInvolucrados.length).toFixed(2)} por persona</p>)}
              </div>

              <div className="pt-2">
                <button onClick={guardarNuevoGasto} className="w-full bg-slate-900 text-white font-black py-4 rounded-2xl text-xs uppercase tracking-widest hover:bg-slate-800 transition-transform shadow-xl shadow-slate-900/20 active:scale-95">{modoEdicion ? 'Actualizar Gasto' : (inputTipo === 'Saldado' ? 'Registrar Pago' : 'Guardar Gasto')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 👥 MODAL NUEVO GRUPO PRO (CON SELECTOR DE AMIGOS) */}
      {mostrarFormularioGrupo && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 print:hidden">
          <div className="bg-white w-full max-w-md rounded-[32px] p-6 md:p-8 shadow-2xl animate-in zoom-in-95 duration-200 border border-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">Nuevo Grupo</h3>
              <button onClick={() => { setMostrarFormularioGrupo(false); setUsuarioNoEncontrado(null); setInvitadosSeleccionados([]); setInputNuevoCorreo(''); }} className="h-8 w-8 bg-slate-100 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors font-bold">&times;</button>
            </div>

            {usuarioNoEncontrado ? (
              <div className="text-center space-y-4 py-2">
                 <div className="text-5xl mb-2">🤷‍♂️</div>
                 <h3 className="text-lg font-black text-slate-900 tracking-tight">Usuario no registrado</h3>
                 <p className="text-sm text-slate-500 font-medium leading-relaxed">
                   Los siguientes correos no existen en CuentasClaras: <span className="font-bold text-slate-800 block mt-2">{usuarioNoEncontrado}</span>
                 </p>
                 <div className="pt-4 flex flex-col gap-2">
                   <button onClick={() => { compartirApp(); setUsuarioNoEncontrado(null); setMostrarFormularioGrupo(false); setInvitadosSeleccionados([]); setInputNuevoCorreo(''); }} className="w-full bg-emerald-600 text-white font-black py-4 rounded-2xl text-xs uppercase tracking-widest hover:bg-emerald-700 transition-transform shadow-xl shadow-emerald-600/20 active:scale-95">
                     Enviarles Invitación
                   </button>
                   <button onClick={() => setUsuarioNoEncontrado(null)} className="w-full text-slate-500 font-bold py-3 text-xs uppercase tracking-widest hover:bg-slate-50 rounded-2xl transition-transform active:scale-95">
                     Volver al formulario
                   </button>
                 </div>
              </div>
            ) : (
              <div className="space-y-5">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Nombre del Grupo</label>
                  <input value={nuevoGrupoNombre} onChange={(e) => setNuevoGrupoNombre(e.target.value)} type="text" placeholder="Ej: Viaje a Talara" className="w-full p-4 bg-slate-50 rounded-2xl text-sm font-bold border border-slate-200 focus:border-emerald-500 outline-none mt-1 transition-colors" />
                </div>
                
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2 block">Añadir Participantes</label>
                  
                  {/* Selector Manual */}
                  <div className="flex gap-2 mb-4">
                    <input 
                      value={inputNuevoCorreo} 
                      onChange={(e) => setInputNuevoCorreo(e.target.value)} 
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregarCorreoManual(); } }}
                      type="email" 
                      placeholder="nuevo@correo.com" 
                      className="flex-1 p-3 bg-slate-50 rounded-xl text-sm font-bold border border-slate-200 focus:border-emerald-500 outline-none transition-colors" 
                    />
                    <button type="button" onClick={agregarCorreoManual} className="bg-slate-900 text-white px-5 rounded-xl font-black text-lg hover:bg-slate-800 transition-colors shadow-md active:scale-95">+</button>
                  </div>

                  {/* Directorio de Amigos */}
                  {listaDirectorioAmigos.length > 0 && (
                    <div className="mb-4">
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Tus Contactos</p>
                      <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto custom-scrollbar pr-1 pb-1">
                        {listaDirectorioAmigos.map(amigo => {
                           const seleccionado = invitadosSeleccionados.includes(amigo.email.toLowerCase());
                           return (
                             <button
                               key={amigo.email}
                               onClick={() => toggleInvitado(amigo.email.toLowerCase())}
                               className={`px-3 py-2 rounded-xl text-xs font-black border transition-all flex items-center gap-2 active:scale-95 ${seleccionado ? 'bg-emerald-500 border-emerald-600 text-white shadow-md' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:shadow-sm'}`}
                             >
                               <span>{seleccionado ? '✓ ' : ''}{obtenerNombreCorto(amigo.email)}</span>
                             </button>
                           )
                        })}
                      </div>
                    </div>
                  )}

                  {/* Lista Final Seleccionada */}
                  {invitadosSeleccionados.length > 0 && (
                     <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-3">Invitados ({invitadosSeleccionados.length})</p>
                        <div className="flex flex-wrap gap-2">
                           {invitadosSeleccionados.map(email => (
                              <span key={email} className="px-2.5 py-1.5 bg-white border border-slate-200 text-slate-700 text-[10px] font-bold rounded-lg flex items-center gap-1.5 shadow-sm">
                                {obtenerNombreCorto(email)}
                                <button onClick={() => toggleInvitado(email)} className="text-slate-400 hover:text-rose-500 bg-slate-50 hover:bg-rose-50 rounded-md h-4 w-4 flex items-center justify-center transition-colors">&times;</button>
                              </span>
                           ))}
                        </div>
                     </div>
                  )}
                </div>

                <div className="pt-4 border-t border-slate-100 flex flex-col gap-2">
                  <button onClick={guardarNuevoGrupo} className="w-full bg-emerald-600 text-white font-black py-4 rounded-2xl text-xs uppercase tracking-widest hover:bg-emerald-700 transition-transform shadow-xl shadow-emerald-600/20 active:scale-95">Crear Grupo Multi-usuario</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ✏️🗑️ MODAL DETALLE TRANSACCIÓN (CON VISOR DE RECIBOS Y SEGURIDAD) */}
      {transaccionSeleccionada && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[130] p-4 print:hidden" onClick={() => {setTransaccionSeleccionada(null); setMostrarConfirmacionEliminarTrx(false);}}>
          {mostrarConfirmacionEliminarTrx ? (
             <div className="bg-white w-full max-w-sm rounded-[32px] p-8 shadow-2xl border-4 border-rose-100 text-center animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
               <div className="h-16 w-16 rounded-full bg-rose-100 flex items-center justify-center text-rose-500 text-3xl mx-auto mb-4">🗑️</div>
               <h3 className="text-xl font-black text-slate-900 mb-2 tracking-tight">¿Eliminar {transaccionSeleccionada.tipo}?</h3>
               <p className="text-sm text-slate-500 mb-8 font-medium leading-relaxed">Este voucher se destruirá y los balances matemáticos del grupo se ajustarán automáticamente.</p>
               <button onClick={ejecutarEliminacionTransaccion} className="w-full py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-transform bg-rose-600 text-white shadow-lg shadow-rose-600/30 hover:bg-rose-700 mb-2 active:scale-95">Sí, Eliminar</button>
               <button onClick={() => setMostrarConfirmacionEliminarTrx(false)} className="w-full text-slate-500 font-bold py-3 text-xs uppercase tracking-widest hover:bg-slate-50 rounded-2xl transition-colors active:scale-95">Cancelar</button>
             </div>
          ) : (
            <div className="bg-white w-full max-w-sm rounded-[32px] p-8 shadow-2xl animate-in zoom-in-95 duration-200 relative overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setTransaccionSeleccionada(null)} className="absolute top-4 right-4 h-8 w-8 bg-slate-50 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 transition-colors font-bold">&times;</button>
              
              <div className="flex flex-col items-center mt-2 mb-6">
                <div className={`h-16 w-16 rounded-2xl mb-4 flex items-center justify-center text-3xl shadow-inner ${transaccionSeleccionada.tipo === 'Saldado' ? 'bg-emerald-50' : 'bg-slate-50'}`}>{transaccionSeleccionada.tipo === 'Saldado' ? '💸' : '🧾'}</div>
                <h3 className="text-2xl font-black text-center text-slate-900 tracking-tight leading-tight">{transaccionSeleccionada.tienda}</h3>
                <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full mt-3 ${transaccionSeleccionada.tipo === 'Saldado' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{transaccionSeleccionada.tipo} {transaccionSeleccionada.editado && <span className="ml-1 text-slate-800">✏️ (Editado)</span>}</span>
              </div>
              
              <div className="space-y-4 border-t border-slate-100 pt-6">
                <div className="flex justify-between items-center"><span className="text-slate-400 font-black text-[10px] uppercase tracking-widest">{transaccionSeleccionada.tipo === 'Saldado' ? 'Pagado por:' : 'Pagado por:'}</span><span className="font-bold text-slate-800 text-sm bg-slate-50 px-3 py-1 rounded-lg">{transaccionSeleccionada.pagadorEmail ? obtenerNombreCorto(transaccionSeleccionada.pagadorEmail) : transaccionSeleccionada.persona}</span></div>
                <div className="flex justify-between items-start"><span className="text-slate-400 font-black text-[10px] uppercase tracking-widest mt-1">{transaccionSeleccionada.tipo === 'Saldado' ? 'Pagado a:' : 'Dividido con:'}</span><div className="text-right flex flex-col items-end gap-1">{transaccionSeleccionada.involucradosEmails && transaccionSeleccionada.involucradosEmails.length > 0 ? (transaccionSeleccionada.involucradosEmails.map(e => <span key={e} className="font-bold text-[10px] uppercase tracking-widest px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md">{obtenerNombreCorto(e)}</span>)) : (<span className="font-bold text-[10px] uppercase tracking-widest px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md">Todos / Legacy</span>)}</div></div>
                <div className="flex justify-between items-center"><span className="text-slate-400 font-black text-[10px] uppercase tracking-widest">{transaccionSeleccionada.editado ? 'Actualizado:' : 'Fecha:'}</span><span className="font-bold text-slate-800 text-sm">{transaccionSeleccionada.fecha} a las {transaccionSeleccionada.hora}</span></div>
                <div className="flex justify-between items-center border-t border-slate-100 pt-5 mt-2">
                   <span className="text-slate-800 font-black uppercase tracking-widest text-xs mt-1">Total:</span>
                   <span className={`text-3xl font-black tracking-tighter ${transaccionSeleccionada.tipo === 'Saldado' ? 'text-emerald-500' : 'text-slate-900'}`}>S/ {transaccionSeleccionada.monto.toFixed(2)}</span>
                </div>
              </div>

              {/* 📸 BOTÓN DE VER RECIBO ACTUALIZADO (NUEVA UX LIGHTBOX) */}
              {transaccionSeleccionada.reciboUrl && (
                 <div className="mt-6">
                   <button onClick={() => setReciboAbierto(transaccionSeleccionada.reciboUrl)} className="w-full flex items-center justify-center gap-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 border border-indigo-200 font-black py-3 rounded-xl uppercase tracking-widest text-[10px] transition-colors shadow-sm active:scale-95">
                     📄 Ver Recibo Adjunto
                   </button>
                 </div>
              )}

              {/* 🔒 BLOQUEO DE SEGURIDAD PARA GRUPOS LIQUIDADOS */}
              {grupos.find(g => g.id === transaccionSeleccionada.grupoId)?.activo ? (
                  <div className="flex gap-3 mt-4 pt-4 border-t border-slate-100">
                     <button onClick={() => setMostrarConfirmacionEliminarTrx(true)} className="flex-1 bg-white border border-rose-200 text-rose-500 hover:bg-rose-50 font-black py-3 rounded-xl uppercase tracking-widest text-[10px] transition-transform active:scale-95 shadow-sm">🗑️ Eliminar</button>
                     {transaccionSeleccionada.tipo === 'Gasto' && <button onClick={() => abrirFormulario(null, transaccionSeleccionada)} className="flex-1 bg-slate-900 text-white hover:bg-slate-800 font-black py-3 rounded-xl uppercase tracking-widest text-[10px] transition-transform active:scale-95 shadow-md">✏️ Editar</button>}
                  </div>
              ) : (
                  <div className="mt-4 pt-4 border-t border-slate-100 text-center">
                     <span className="inline-block w-full bg-slate-100 text-slate-400 font-black py-3 rounded-xl uppercase tracking-widest text-[10px] border border-slate-200">🔒 Grupo cerrado (Solo Lectura)</span>
                  </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 🛡️ MODAL CONFIRMACIÓN LIQUIDACIÓN DEUDA */}
      <ModalConfirm 
        show={pagoPendienteConf !== null} 
        icon="💸" 
        color="emerald" 
        title="¿Confirmar Liquidación?" 
        msg={`Vas a registrar un pago de S/ ${pagoPendienteConf?.monto.toFixed(2)} a ${obtenerNombreCorto(pagoPendienteConf?.acreedorEmail)}. Esta acción actualizará los balances del grupo.`}
        btnText="Sí, Registrar Pago" 
        onConfirm={() => {
          liquidarDeudaOptimizada(pagoPendienteConf, grupos.find(g => g.id === grupoActivoId)?.nombre);
          setPagoPendienteConf(null);
        }} 
        onCancel={() => setPagoPendienteConf(null)}
      />

      <ModalConfirm 
        show={mostrarConfirmacionLiquidar} icon="🔒" color="amber" title="¿Archivar Grupo?" 
        msg="El grupo pasará al archivo histórico. Ya no se podrán agregar nuevos gastos ni saldar deudas en este espacio."
        btnText="Sí, Archivar Grupo" onConfirm={ejecutarLiquidacionGrupo} onCancel={() => setMostrarConfirmacionLiquidar(false)}
      />

      <ModalConfirm 
        show={mostrarConfirmacionEliminar} icon="⚠️" color="rose" title="¿Eliminar Grupo?" 
        msg={<>Esta acción destruirá todo el historial de deudas para todos los integrantes. <span className="font-black text-rose-500 block mt-2">No se puede deshacer.</span></>}
        btnText="Sí, Destruir Grupo" onConfirm={ejecutarEliminacionGrupo} onCancel={() => setMostrarConfirmacionEliminar(false)}
      />

      <style>{`.custom-scrollbar::-webkit-scrollbar { width: 6px; } .custom-scrollbar::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 10px; } .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }`}</style>
    </div>
  );
}